import {
  root,
  $win,
  $doc,
  $html,
  $body,
  inputs
} from './01.constant.mjs';

import {
  debug,
  debounce,
  throttle,
  isObject,
  isCssSize,
  isDeviceDarkmode
} from './02.util.mjs';

import {
  toBoolean,
  toArray
} from './03.helper.mjs';


/*==============================================================*/
// ? method()  퍼블릭 / 외부 호출 가능 (public 키워드 X)
// ? #method()  프라이빗 / 외부 호출 불가 / 퍼블릭 돕는 내부 함수 (private 키워드 X)
// ? static method()   인스턴스 없이 바로 호출 가능 (예) Scroll.lock()
// ? static #mothod()  위 퍼블릭 static method() 돕는 내부 함수에 필수
// ? static #field  클래스 내 공유 변수 / 다른 static 메서드 참조 시 static 필수
// ? - 참조 방법 : ClassName.#field
// ? #field  각 인스턴스마다 생성되는 변수 (예) 스톱워치 클래스에서 시작 시간 등
// ? - 참조 방법 : this.#field
/*==============================================================*/


export class Cssvar {
  // get 1 value for 1 cssvar
  static get(name) {
    return getComputedStyle(root).getPropertyValue(name.trim()).trim();
  }
  // set cssvar @ :root
  //  @param {string|Object} props
  //  @ - string '--var-name' or Object { '--var-name': 'value' }
  //  @param {string} [value] - props가 문자열일 경우 값
  static set(props, value = null) {
    const properties = isObject(props) ? props : { [props]: value };
    Object.entries(properties).forEach(([key, val]) => {
      // key, value 유효 값일 때만 setProperty 실행
      if ( key && val !== undefined && val !== null ) {
        root.style.setProperty(key.trim(), String(val).trim());
      }
    });
  }

  // delete cssvar @ :root
  // * cannot delete ones defined @ css file
  // @param {string|Array<string>} props - 제거할 변수명
  // @ - 배열 또는 쉼표/공백으로 구분된 문자열
  static delete(props) {
    const properties = toArray(props);
    properties.forEach(prop => {
      // prop 유효 문자열일 때만 removeProperty 실행
      if ( typeof prop === 'string' && prop ) {
        root.style.removeProperty(prop.trim());
      }
    });
  }
}


export class Scroll {
  static #isLocked = false;
  static #touchStartY = 0;
  static #lockExcept = null;
  static #keyScrollEvents = [
    ' ', 'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', 'Tab'
  ];
  static #listeners = [
    ['keydown', Scroll.#handleKey, { passive: false }],
    ['wheel', Scroll.#handleWheel, { passive: false }],
    ['touchmove', Scroll.#handleTouchMove, { passive: false }],
    ['touchstart', Scroll.#handleTouchStart, { passive: true }]
  ];

  static #cancelablePrevent(event) {
    if ( event.cancelable ) event.preventDefault();
  }

  static #isInnerScrollable(element, deltaY) {
    const isAtTop = element.scrollTop <= 0;
    const isAtBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 1;

    if ( deltaY < 0 && !isAtTop ) return true;
    if ( deltaY > 0 && !isAtBottom ) return true;
    return false;
  }

  // ==========================================
  // 🌟 [핵심 추가] 실제 스크롤이 발생하는 내부 요소를 찾는 헬퍼
  // ==========================================
  static #getScrollableNode(target, boundaryNode) {
    let current = target;

    while ( current && current !== document ) {
      // 1. 현재 요소의 내용물이 껍데기보다 큰지 (스크롤 발생 조건) 확인
      if ( current.scrollHeight > current.clientHeight ) {
        // 2. CSS에 overflow-y 속성이 auto나 scroll로 걸려있는지 확인
        const overflowY = window.getComputedStyle(current).overflowY;
        if ( overflowY === 'auto' || overflowY === 'scroll' ) {
          return current; // 진짜 스크롤 요소를 찾음! (.address-results 등)
        }
      }

      if ( current === boundaryNode ) break; // 경계(다이얼로그)에 도달하면 탐색 종료
      current = current.parentNode;
    }

    return boundaryNode; // 못 찾으면 기본 경계 요소 반환
  }

  static #handleKey(event) {
    // 🌟 [핵심 추가] 사용자가 입력창에서 텍스트를 타이핑하거나 커서를 이동 중일 때는 스크롤 간섭을 즉시 중단!
    const targetTag = event.target.tagName;
    if ( ['INPUT', 'TEXTAREA', 'SELECT'].includes(targetTag) || event.target.isContentEditable ) {
      return;
    }

    if ( !Scroll.#keyScrollEvents.includes(event.key) ) return;

    if ( Scroll.#lockExcept ) {
      const activeElem = document.activeElement;
      if ( activeElem ) {
        const $boundary = jQuery(activeElem).closest(Scroll.#lockExcept);

        if ( $boundary.be() ) {
          // 🌟 [수정] 다이얼로그 대신 진짜 스크롤 요소 탐색
          const scrollNode = Scroll.#getScrollableNode(activeElem, $boundary.get(0));
          let deltaY = 0;

          if ( event.key === 'Home' ) {
            if ( scrollNode.scrollTop > 0 ) scrollNode.scrollTop = 0;
            event.preventDefault();
            return;
          }
          if ( event.key === 'End' ) {
            if ( scrollNode.scrollTop + scrollNode.clientHeight < scrollNode.scrollHeight ) {
              scrollNode.scrollTop = scrollNode.scrollHeight;
            }
            event.preventDefault();
            return;
          }

          const KeyMaps = {
            'ArrowDown': 40,
            ' ': 40,
            'ArrowUp': -40,
            'PageDown': scrollNode.clientHeight,
            'PageUp': -scrollNode.clientHeight
          };
          deltaY = KeyMaps[event.key] ?? 0;

          // 🌟 [수정] 찾은 진짜 스크롤 요소로 경계 체크
          if ( Scroll.#isInnerScrollable(scrollNode, deltaY) ) return;
        }
      }
    }

    event.preventDefault();
  }

  static #handleWheel(event) {
    if ( !Scroll.#lockExcept ) { event.preventDefault(); return; }

    const $boundary = jQuery(event.target).closest(Scroll.#lockExcept);
    if ( $boundary.be(false) ) { event.preventDefault(); return; }

    // 🌟 [수정] 마우스 휠 타겟부터 다이얼로그 사이의 진짜 스크롤 요소 탐색
    const scrollNode = Scroll.#getScrollableNode(event.target, $boundary.get(0));

    // 🌟 [수정] 찾은 진짜 스크롤 요소로 검사
    if ( Scroll.#isInnerScrollable(scrollNode, event.deltaY) ) return;

    event.preventDefault();
  }

  static #handleTouchMove(event) {
    if ( !Scroll.#lockExcept ) { Scroll.#cancelablePrevent(event); return; }
    if ( !event.touches || !event.touches.length ) {
      Scroll.#cancelablePrevent(event); return;
    }

    const $boundary = jQuery(event.target).closest(Scroll.#lockExcept);
    if ( $boundary.be(false) ) { Scroll.#cancelablePrevent(event); return; }

    const touchY = event.touches[0].clientY;
    const deltaY = Scroll.#touchStartY - touchY;

    // 🌟 [수정] 터치 타겟부터 다이얼로그 사이의 진짜 스크롤 요소 탐색
    const scrollNode = Scroll.#getScrollableNode(event.target, $boundary.get(0));

    // 🌟 [수정] 찾은 진짜 스크롤 요소로 검사
    if ( Scroll.#isInnerScrollable(scrollNode, deltaY) ) {
      Scroll.#touchStartY = touchY; return;
    }

    Scroll.#cancelablePrevent(event);
  }

  static #handleTouchStart(event) {
    if ( event.touches && event.touches.length ) {
      Scroll.#touchStartY = event.touches[0].clientY;
    }
  }

  static #toggleListeners(method) {
    Scroll.#listeners.forEach(([event, handler, options]) =>
      window[method](event, handler, options)
    );
  }

  static isLocked() { return Scroll.#isLocked; }

  static lock(lockExcept = null) {
    if ( Scroll.#isLocked ) return;
    Scroll.#lockExcept = lockExcept;
    Scroll.#toggleListeners('addEventListener');
    Scroll.#isLocked = true;
  }

  static unlock() {
    if ( !Scroll.#isLocked ) return;
    Scroll.#toggleListeners('removeEventListener');
    Scroll.#lockExcept = null;
    Scroll.#isLocked = false;
    $win.trigger('scroll:unlocked');
  }
}


export class Darkmode { // * CSS required
  static #localName = 'isDarkmode';
  static #colorScheme = '(prefers-color-scheme: dark)';
  static #transitionTimer = null; // ? 단시간 내 모드 전환 시 대비

  static is() {
    const isDarkmodeLocal = window.localStorage.getItem(Darkmode.#localName);
    return isDarkmodeLocal === null
      ? isDeviceDarkmode() : toBoolean(isDarkmodeLocal);
  }

  // selector = 'checkbox[data-toggle-darkmode]'
  constructor(selector) { this.selector = selector; }

  #init(isDarkmode) {
    $html.datattr({ pageLoading: '' }); // * 트랜지션 일시 정지

    window.localStorage.setItem(Darkmode.#localName, isDarkmode);
    $html.datattr({ isDarkmode }); // * 실제 모드 변경 시점
    jQuery(this.selector).checked(isDarkmode);

    // 단시간 내 연속 클릭 시 타이머 중복 실행 방지
    if ( Darkmode.#transitionTimer ) clearTimeout(Darkmode.#transitionTimer);

    // 스타일 렌더링할 시간(50ms)을 번 후, 트랜지션 원복
    Darkmode.#transitionTimer = setTimeout(() => {
      $html.removeDatattr('pageLoading');
      Darkmode.#transitionTimer = null; // 타이머 리셋
    }, 50); // 50ms 버퍼
  }

  watch() {
    this.#init(Darkmode.is()); // initialize
    $doc.on('change', this.selector, (event) =>
      this.#init(jQuery(event.currentTarget).checked())
    );
    window.matchMedia(Darkmode.#colorScheme)
      .addEventListener('change', (event) => this.#init(event.matches));
  }
}


export class Language { // * CSS required
  constructor(selectors) { // selectors = 'select[data-select-language]'
    this.selectors = selectors;
  }

  async #change(language) { // 서버에 언어 변경 요청하고 페이지 reload
    const currentHash = window.location.hash;
    try {
      const data = await jQuery.ajax({
        url: '/js/ajax/language.php',
        method: 'POST',
        data: { language },
        dataType: 'json',
      });
      // 성공 시 반환된 URL 이동 (해시 유지)
      window.location.href = data + currentHash;
    }
    catch (error) {
      Dialog.error(`Language change failed: ${error.status}`);
    }
  }

  watch() {
    $doc.on('change', this.selectors, (event) =>
      this.#change(jQuery(event.currentTarget).value().toLowerCase())
    );
  }
}


export class Header { // * CSS required
  static #scrollEvents = 'scrollup scrollupstop scrolldown scrolldownstop';
  #headerHeightCache = 0;
  #savedScrollY = 0; // ? 스크롤 위치 저장 변수

  constructor(selectors) { // selectors = 'body > header'
    this.selectors = selectors;
    this.$header = jQuery(selectors);
    this.#savedScrollY = window.scrollY; // 초기화
  }

  #cacheHeaderHeight() {
    this.#headerHeightCache = this.$header.is(':visible')
      ? this.$header.outerHeight() : 0;
  }

  #slideUp(doHide) {
    this.$header.css('translate', doHide ? '0 -100%' : '0 0');}

  #updatePosition(event = null) { // * 헤더 위치 결정 (초기화 시 event = null)
    const headerHeight = this.#headerHeightCache;
    let doHide;

    if ( event === null ) {
      // 현재 스크롤 위치가 헤더 높이보다 크면 숨김 처리 (새로 고침 시 스크롤 유지 대응)
      // 맨 위라면(0) 당연히 보임
      doHide = window.scrollY > headerHeight;
    }
    else {
      const isScrollDown = event.type.startsWith('scrolldown');
      doHide = isScrollDown && window.scrollY > headerHeight;
    }

    this.#slideUp(doHide);
  }

  // ? 헤더 내부 포커스 시 스크롤 점프 방지
  // ? - html { scroll-padding-block-start } 설정 있을 시 스크롤 업 되는 현상 방지
  #preventScrollJump() {
    $doc.on('keydown', (event) => { // * 문서 전체 Tab 키 감지 후 스크롤 위치 저장
      if ( event.key === 'Tab' ) this.#savedScrollY = window.scrollY;
    });
    // * 헤더 내부 마우스 클릭 감지 (이벤트 위임 불필요, 헤더에 직접 바인딩)
    this.$header.on('mousedown', () => {
      this.#savedScrollY = window.scrollY;
    });

    // * 헤더 내부로 포커스 진입 시 위치 복원
    // * - focusin 이벤트는 버블링되므로 헤더 내부 모든 요소의 포커스를 감지함
    this.$header.on('focusin', () => {
      window.scrollTo(window.scrollX, this.#savedScrollY);
    });
  }

  watch() {
    this.#cacheHeaderHeight(); // 페이지 로드 시 높이를 먼저 측정하고 캐시
    this.#updatePosition(null); // 초기 위치 설정
    $win.on(Header.#scrollEvents, throttle((event) =>
      this.#updatePosition(event)
    ));

    $win.on('resize', debounce(() => {
      this.#cacheHeaderHeight(); this.#updatePosition(null);
    }));

    // 오프캔버스 상태가 되면 헤더 보이게
    $win.on('offcanvas:activated', () => this.#slideUp(false));
    // 오프캔버스 상태가 해제되면  현재 스크롤 위치 기준 헤더 위치 재계산 후 업데이트
    $win.on('offcanvas:deactivated', () => this.#updatePosition(null));

    this.#preventScrollJump();
  }
}


export class Offcanvas { // * CSS required
  static #all = '[data-offcanvas]';
  static #shown = '[data-show-offcanvas]';
  static #backdrop = '[data-offcanvas-backdrop]';
  static #submenuTrigger = 'li:has(div > ul) > a';
  static #submenuState = '[data-show-submenu]';

  constructor(selectors) { // selectors = 'button[data-toggle-offcanvas]'
    this.selectors = selectors;
  }

  #show(event) {
    const side = jQuery(event.currentTarget).data('toggleOffcanvas');
    const $offcanvas = jQuery(`[${Offcanvas.#all.slice(1, -1)}='${side}']`);
    // * 열려고 하는 오프캔버스 내에 선택 아이템을 가지고 있는 서브 메뉴 컨테이너를 취함
    const $submenuContainer = $offcanvas.find('li.selected').closest('div');
    const isSubmenuSelected = $submenuContainer.be();

    // * 선택 아이템이 있는 서브 메뉴가 있다면 트랜지션 임시 소거 후 속성 부여
    // ? - 이렇게 한 이유는 오프캔버스에 서브 메뉴가 선택된 상태 있는 채로 데스크탑 전환 시
    // ? - 서브 메뉴 grid-template-rows: 1fr -> 0fr 전환에 따른 트샌지션이 발생하는데
    // ? - 이게 영 이상하여 현재와 같은 오프캔버스 클래스를 구현하게 됨.
    // ? - 해당 현상 거의 잡았으나, 오프캔버스 변곡점(Breakpoint)에서 한두 픽셀 씩
    // ? - 리사이즈하면 여전히 보이긴 함 (특히, 오프캔버스 연 상태로 데스크탑 전환 시)
    // ? - 그러나 위와 같은 케이스는 사실 거의 드물기 때문에, 이 즈음에서 마무리하기 함
    if ( isSubmenuSelected ) {
      $submenuContainer.css('transition', 'none')
        .prev('a').datattr({ showSubmenu: true });
    }

    Scroll.lock($offcanvas); // 스크롤 비활성화

    // 기존 백드롭 없을 때만 백드롭 생성 (아니면 스페이스바 누를 때마다 무한 백드롭 생성됨)
    if ( jQuery(Offcanvas.#backdrop).be(false) ) {
      $offcanvas.fadeInAfter(
        `<div ${Offcanvas.#backdrop.slice(1, -1)}></div>`
      );
    }

    setTimeout(() => { // ? 랜더링 순서 보장하기 위해
      $offcanvas.datattr({ showOffcanvas: '' }); // 오프캔버스 메뉴 열기
      // * 임시 소거했던 트랜지션 복구
      if ( isSubmenuSelected ) $submenuContainer.removeCss('transition');
    }, 0);
  }

  #hide() {
     // 열려 있는 오프캔버스만 찾음 (닫힌 건 이미 이 메서드로 인해 아래 수행했으므로)
     // ? - 아래 구문은 리사이즈 시 무조건 오프캔버스를 닫도록한 아래 코드에 대한 방어 구문
     // ? - 라사이즈는 초당 수백 번 발생하는데, 최초 첫 리사이즈에 대해 이 코드 수행 후
     // ? - 이후엔 이 방어 구문으로 #hide() 메서드 호출되어도 즉시 종료됨
    const $offcanvas = jQuery(Offcanvas.#shown);
    if ( $offcanvas.be(false) ) return; // 열린 것 없으면 중지

    // 서브 메뉴 상태를 가지고 있는 모든 트리거 찾음
    // ? - 사용자가 .selected 아닌 다른 서브 메뉴 연 상태에서 오프캔버스 닫을 수 있으므로
    const $submenuTrigger = $offcanvas.find(`a${Offcanvas.#submenuState}`);

    // * 트리거 상태 제거
    if ( $submenuTrigger.be() ) $submenuTrigger.removeDatattr('showSubmenu');

    Scroll.unlock(); // 스크롤 활성화 : 아래 랜더링 순서 기다리지 말고 즉시 스크롤 복구

    setTimeout(() => { // ? 랜더링 순서 보장하기 위해
      $offcanvas.removeDatattr('showOffcanvas'); // 오프캔버스 닫기
      jQuery(Offcanvas.#backdrop).fadeOutRemove(); // 백드롭 제거
    }, 0);
  }

  #toggleSubmenu(event) {
    event.preventDefault(); // * 핵심 : 서브 메뉴 있는 메뉴 아이템은 실제 클릭 안 되게
    const $trigger = jQuery(event.currentTarget);
    // data-show-submenu 속성 값을 반전시켜 토글
    // ? data() 함수는 캐시 값 사용 = 뷰포트 전환 시 상태 꼬일 수도 = attr() 이용
    $trigger.toggleBooleanDatattrIf(
      'showSubmenu', !toBoolean($trigger.attr('data-show-submenu'))
    );
  }

  watch() {
    $doc.on('click', this.selectors, (event) => this.#show(event));
    $doc.on('click', Offcanvas.#backdrop, () => this.#hide());
    $doc.on(
      'click',
      // 열린 오프캔버스 서브 메뉴만 타겟
      `${Offcanvas.#shown} ${Offcanvas.#submenuTrigger}`,
      (event) => this.#toggleSubmenu(event)
    );

    // 화면 리사이즈 시 즉시 오프캔버스 닫기
    // ? - 데스크탑 환경에선 리사이즈 케이스가 많지 않고, 오프캔버스 열린 상태도 거의 없음
    // ? - 모바일 환경에선 portrait/landscape 상태 전환이 대부분
    // ? - 이때 오프캔버스 열린 상태로 전환되면 이상한 레이아웃 나오기도 해서 그냥 닫기로
    $win.on('resize', () => this.#hide());
  }
}


export class OffcanvasEvent {
  #isActive; // * 현재 상태 (true: active/모바일, false: inactive/데스크탑)

  constructor(selector) { // selector = 'button[data-toggle-offcanvas]'
    this.selector = selector;
    this.$trigger = jQuery(this.selector).first(); // 버튼 중 하나만 감시하면 됨
    this.#isActive = this.$trigger.is(':visible'); // 초기 상태 설정
  }

  #triggerEvent(isActive) {
    $win.trigger(`offcanvas:${isActive ? 'activated' : 'deactivated'}`);
  }

  // 현재 버튼의 :visible 상태 확인 후 저장된 상태(#isActive)와 다를 경우 이벤트 발생
  #checkState() {
    const isNowActive = this.$trigger.is(':visible');

    if ( isNowActive === this.#isActive ) return; // 상태 같으면 아무것도 안 함

    this.#isActive = isNowActive; // 상태가 변경되었으므로, 새 상태를 저장
    this.#triggerEvent(isNowActive); // 새 상태에 맞는 커스텀 이벤트 발생시킴
  }

  isActive() { return this.#isActive; } // 현재 오프캔버스 활성화 상태 여부 반환

  watch() {
    this.#triggerEvent(this.#isActive); // 페이지 로드 시 초기 상태 이벤트 1회 발생
    $win.on('resize', debounce(() => this.#checkState()));
  }
}


export class Table { // * CSS required
  static #container = '[data-table-responsive-container]';
  #$tables;

  constructor(selectors = 'table') { this.#$tables = jQuery(selectors); }

  #initContainer($table) { // 단일 테이블 container 상태를 업데이트
    const $container = $table.parent(Table.#container);
    if ( !$container.be() ) return;

    const container = $container.get(0);
    const hasOverflow = container.scrollWidth > container.clientWidth;

    $container.toggleDatattrIf(
      'tableResponsiveContainer', hasOverflow, ['scrollable', '']
    );
  }

  #initAllContainers() { // 모든 테이블 container 상태 업데이트
    this.#$tables.each((_, elem) => this.#initContainer(jQuery(elem)));
  }

  watch() {
    this.#initAllContainers(); // 페이지 로드 시 모든 대상 테이블의 초기 상태 설정
    // 창 크기 변경 시, 성능을 위해 debounce 통해 모든 테이블 상태를 다시 확인
    $win.on('resize', debounce(() => this.#initAllContainers()));
  }
}


export class Form {
  constructor(selectors = 'form') { this.selectors = selectors; }

  // 범용 폼 리셋 이벤트 처리 로직 분리
  #reset(event) {
    const $form = jQuery(event.currentTarget);

    // 1. 질문자님의 순정 롤백 실행 (HTML 속성 복원 및 제이쿼리 캐시 초기화)
    $form.find(':input').rollback();

    // 2. 폼 트래커 상태 초기화 (is-changed 찌꺼기 제거)
    $form.find('.is-changed').removeClass('is-changed');
    $form.trigger('tracker:update', { isChanged: false });

    // 3. 브라우저의 네이티브 리셋(값 비우기 등)이 완전히 끝난 직후 UI 동기화
    setTimeout(() => {
      // 이제 핸들러들에 originalEvent 방어막이 생겼으므로,
      // 여기서 발생시킨 change 이벤트가 값을 지워버리는 대참사가 일어나지 않습니다!
      $form.find(':input').trigger('change');
    }, 0);
  }

  // 🌟 [NEW] 폼 요소들의 초기 '값/상태'를 스냅샷으로 찍는 헬퍼 메서드
  #takeSnapshot($form) {
    // 정의해 주신 inputs.all 상수 활용!
    $form.find(inputs.all).each(function() {
      const $el = jQuery(this);
      this.__initialState = $el.is(inputs.choice) ?
        $el.checked() : $el.value();
    });
  }

  // 🌟 [NEW] 폼 요소 변경 감지 및 클래스 토글
  #handleTrack(event) {
    const el = event.currentTarget;
    const $el = jQuery(el);

    // 동적으로 추가된 요소라 스냅샷이 없다면 즉시 생성
    if (el.__initialState === undefined) {
      el.__initialState = $el.is(inputs.choice) ?
        $el.checked() : $el.value();
    }

    const currentState = $el.is(inputs.choice) ? $el.checked() : $el.value();
    const isChanged = currentState !== el.__initialState;

    // 🌟 원본과 다르면 추가, 원상복구 되면 즉시 제거
    $el.toggleClass('is-changed', isChanged);

    // 🌟 [보너스] 폼 껍데기 자체에 이벤트를 던져서, 나중에 MyProfile 클래스 등에서
    // "이 폼 안에 하나라도 변경된 게 있어?"를 한 줄로 쉽게 캐치할 수 있게 합니다.
    const $form = $el.closest('form');
    const hasAnyChanges = $form.find('.is-changed').length > 0;

    $form.trigger('tracker:update', { isChanged: hasAnyChanges });
  }

  // 🌟 [NEW] 커스텀 폼 요소들의 진짜 데이터(Hidden)를 자동 치환하여 FormData 반환
  static fetchFormData($form) {
    if ( !$form.be() ) return new FormData();

    const formData = new FormData($form.get(0));

    // 특수 인풋들을 찾아 진짜 데이터로 덮어쓰기
    $form.find('[data-autocomplete], [data-datetimer]').each((_, elem) => {
      const $el = jQuery(elem);
      const name = $el.attr('name');
      if ( !name ) return;

      const selectedValue = $el.is('[data-autocomplete]')
        ? $el.data('autocompleteSelected')
        : $el.data('datetimerSelected');

      // 💡 [개선 포인트] 값이 빈 문자열('')인 경우(유저가 입력을 지운 경우)도
      // 정확히 서버로 전달하기 위해 undefined가 아닐 때만 덮어씌웁니다.
      if ( selectedValue !== undefined ) formData.set(name, selectedValue);
    });

    return formData;
  }

  watch() {
    // 폼 렌더링 직후 모든 인풋들의 초기 속성 백업
    jQuery(this.selectors).each((_, form) => {
      const $form = jQuery(form);

      $form.find(':input').each(function() {
        const attrMap = new Map();
        Array.from(this.attributes).forEach(attr => attrMap.set(attr.name, attr.value));
        this.__initialAttrs = attrMap;
      });

      // 🌟 [NEW] data-tracker 속성이 있는 폼만 찾아내어 추적용 스냅샷 굽기!
      if ( $form.is('[data-tracker]') ) {
        this.#takeSnapshot($form);
      }
    });

    $doc.on('reset', this.selectors, (event) => this.#reset(event));

    // 🌟 [NEW] 트래킹 대상 폼 내부의 입력 요소들만 변화를 감지 (이벤트 위임)
    $doc.on('input change', `form[data-tracker] ${inputs.all}`, (event) => this.#handleTrack(event));
  }
}


export class Fieldset {
  constructor(selectors = 'fieldset') {
    this.selectors = selectors;
    this.inputInFieldset = `${this.selectors} :input:not(legend :input)`;
  }

  #updateStatus($fieldset) {
    // ? 하드코딩으로 명시적 잠금을 건 필드셋은 자동 동기화 로직에서 완전히 제외합니다.
    if ( $fieldset[0].hasAttribute('data-lock-update-status') ) return;

    const $allInputs = $fieldset.find(':input').not('legend :input'); // 🌟 규칙: jQuery 배열 변수명 복수형 처리

    if ( $allInputs.be(false) ) {
      $fieldset.enable();
      return;
    }

    const $disabledInputs = $allInputs.filter((_, element) =>
      // 🌟 [핵심 수정 1] 바깥쪽 필드셋이 잠겨있으면 자식의 prop('disabled')도 무조건 true가 되므로,
      // 부모의 간섭을 받지 않는 엘리먼트 자체의 순수 'disabled' 속성 유무만 정밀 검사합니다.
      element.hasAttribute('disabled')
    );
    const isAllDisabled = $allInputs.length === $disabledInputs.length;

    $fieldset.disabled(isAllDisabled);
  }

  watch() {
    jQuery(this.selectors).each((_, element) =>
      this.#updateStatus(jQuery(element))
    );

    jQuery(document).on('propchange', this.inputInFieldset, (event, data) => {
      if ( data && data.prop === 'disabled' ) {
        // 🌟 [핵심 수정 2] closest() 대신 parents()를 사용하여 나를 감싸고 있는
        const $fieldsets = jQuery(event.currentTarget).parents(this.selectors);

        // 감싸고 있는 모든 필드셋의 상태를 하위에서 상위 방향으로 순차적으로 갱신합니다.
        $fieldsets.each((_, element) => {
          this.#updateStatus(jQuery(element));
        });
      }
    });
  }
}


export class Input { // * CSS required
  static #PATTERN_ALIASES = {
    'latin': {
      native: "[a-zA-Z\\u00C0-\\u024F\\s\\-']+",
      negative: /[^a-zA-Z\u00C0-\u024F\s\-']/g
    },
    'latin-extend': {
      native: "[a-zA-Z0-9\\u00C0-\\u024F\\s\\-',.\\/#&\\(\\)\\u00B0\\u00AA\\u00BA]+",
      negative: /[^a-zA-Z0-9\u00C0-\u024F\s\-',.\/#&\(\)\u00B0\u00AA\u00BA]/g
    },
    'ko': {
      native: "[ㄱ-ㅎㅏ-ㅣ가-힣\\s]+",
      negative: /[^ㄱ-ㅎㅏ-ㅣ가-힣\s]/g
    },
    'ja': {
      native: "[\\u3040-\\u309F\\u30A0-\\u30FF\\u4E00-\\u9FAF\\u3000-\\u303F\\s]+",
      negative: /[^\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF\u3000-\u303F\s]/g
    },
    'en-num': {
      native: "[a-zA-Z0-9\\s]+",
      negative: /[^a-zA-Z0-9\s]/g
    },
    'num': {
      native: "[0-9]+",
      negative: /[^0-9]/g
    },
    'tel': {
      native: "[0-9\\s\\-\\+\\(\\)]+",
      negative: /[^0-9\s\-\+\(\)]/g
    },
    'email': {
      native: "[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}",
      negative: /[^a-zA-Z0-9.\-_@+]/g,
      positive: /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/
    },
    'password': {
      // 🌟 [추가] 브라우저 네이티브 Submit 통과용 (최소 8자리, 영문+숫자 포함)
      native: "(?=.*[a-zA-Z])(?=.*[0-9]).{8,}",
      // * 영문, 숫자, 특수기호만 허용 (스페이스바, 한글, 이모지 등 비표준 문자 원천 차단)
      negative: /[^\x21-\x7E]/g,
      // * 영문+숫자 포함 8자리 이상 (특수문자 자유)
      positive: /^(?=.*[A-Za-z])(?=.*\d).{8,}$/
    }
  };

  static #DEFAULT_ERROR_MSG = {
    'ko': '허용되지 않는 문자입니다.', 'en': 'Invalid character.',
    'ja': '許可されていない文字です。', 'fr': 'Caractère non autorisé.'
  };

  static #DEFAULT_REQ_MSG = {
    'ko': '필수 입력 항목입니다.', 'en': 'This field is required.',
    'ja': '必須項目です。', 'fr': 'Ce champ est obligatoire.'
  };

  constructor(selectors = inputs.textAll) { this.selectors = selectors; }

  // 🌟 [NEW] 통합 에러 발생 API (외부 호출 가능)
  static setError($input, msg, autoHideMs = 0) {
    if ( !$input.be() || !msg ) return;

    $input.aria('invalid', 'true'); // 1. 접근성 및 UI 에러 상태 명시
    Tooltip.create($input, msg, 'error'); // 2. 툴팁 생성
    clearTimeout($input.data('tooltipTimer')); // 3. 기존 타이머 정리
    // 4. autoHideMs가 0보다 크면 일정 시간 후 자동 삭제 (패턴 필터링용)
    if ( autoHideMs > 0 ) {
      const timer = setTimeout(() => Input.clearError($input), autoHideMs);
      $input.data('tooltipTimer', timer);
    }
  }

  // 🌟 [NEW] 통합 에러 해제 API (외부 호출 가능)
  static clearError($input) {
    if ( !$input.be() ) return;

    // 에러 상태가 아닐 때는 불필요한 DOM 조작 방지
    if ( $input.aria('invalid') !== 'true' ) return;

    $input.aria('invalid', '');
    clearTimeout($input.data('tooltipTimer'));
    Tooltip.remove();
  }

  #init($input) {
    // 🌟 [수정] 함수 전체를 막지 않고, 오직 pattern 치환만 단 1회 실행되도록 가드 범위를 좁힘
    if ( !$input.data('patternInitialized') ) {
      $input.data('patternInitialized', true); // 플래그 굽기

      const patternStr = $input.attr('pattern');
      if ( patternStr && Input.#PATTERN_ALIASES[patternStr] ) {
        const alias = Input.#PATTERN_ALIASES[patternStr];
        $input.attr('pattern', alias.native);
        $input.data('negativeRegex', alias.negative);
      }
    }

    // 🌟 [수정] 셀렉트 박스의 플레이스홀더 처리 로직
    let isValuePresent = false;

    if ( $input.is('select') ) {
      const $selectedOption = $input.find('option:selected');
      // 옵션 존재하면서, 그 옵션에 disabled 속성도 없고 hidden 속성도 없을 때만 true!
      isValuePresent = $selectedOption.length > 0 && !$selectedOption.is(':disabled, [hidden]');
    }
    else isValuePresent = !!$input.value();

    $input.toggleDatattrIf('hasValue', isValuePresent);

    this.#checkSelectDisabledState($input);
    this.#handleFileChange($input);

    const $icons = $input.siblings('[data-icon-action]');
    $icons.off('click.action').on('click.action', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.#handleIconAction(event);
    });
  }

  // <select> 내 유효한 option 상태 검사
  #checkSelectDisabledState($select) {
    if ( !$select.is('select') ) return;
    const $validOptions = $select.find('option:not([hidden])');

    if ( $validOptions.be() ) {
      const isAllDisabled = $validOptions.filter(':not(:disabled)').be('not');
      if ( isAllDisabled ) {
        if ( !$select.disabled() ) {
          $select.datattr({ autoDisabled: true }).disable();
        }
      }
      else {
        if ( $select.data('autoDisabled') ) {
          $select.removeDatattr('autoDisabled').enable();
        }
      }
    }
  }

  #handlePreventEdit(event) {
    const $input = jQuery(event.currentTarget);

    if ( event.type === 'focusin' ) {
      $input.data('preventEditValue', $input.value());
      return;
    }

    if ( event.type === 'input' ) {
      // 🌟 [수정] 스크립트가 발생시킨 가짜 input 이벤트면 값을 비우는 참사 막고 통과시킴!
      if ( !event.originalEvent ) return;

      $input.value($input.data('preventEditValue') || '');
      return;
    }

    const allowedKeys = [
      'Tab', 'Shift', 'Enter', 'Escape',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'
    ];

    if ( event.type === 'keydown' && allowedKeys.includes(event.key) ) {
      return;
    }

    event.preventDefault();
  }

  #handlePreventSpaceKeydown(event) {
    if ( event.key === ' ' || event.code === 'Space' ) {
      event.preventDefault();
    }
  }

  #handlePreventSpaceInput(event) {
    const $input = jQuery(event.currentTarget);
    const val = $input.value();
    if ( /\s/.test(val) ) $input.value(val.replace(/\s/g, ''));
  }

  #handleDisabledChange(event, data) {
    if ( !data || data.prop !== 'disabled' ) return;
    if ( event.target !== event.currentTarget ) return;

    const $input = jQuery(event.currentTarget);
    $input.siblings('[data-toggle-label]').each((_, elem) => {
      const $sibling = jQuery(elem);
      const htmlCurrent = $sibling.html();
      const htmlToggle = $sibling.data('toggleLabel');
      if ( htmlToggle ) {
        $sibling.html(htmlToggle).datattr({ toggleLabel: htmlCurrent });
      }
    });
  }

  // * 네거티브 패턴 검사 (타이핑 즉시)
  #filterPattern(event) {
    const isComposing = event.originalEvent?.isComposing;
    if ( event.type === 'input' && isComposing ) return;

    const $input = jQuery(event.currentTarget);
    const val = $input.value();

    if ( !val ) {
      Tooltip.remove();
      return;
    }

    let negativeRegex = $input.data('negativeRegex');
    const patternStr = $input.attr('pattern');

    if ( !negativeRegex && patternStr ) {
      if ( Input.#PATTERN_ALIASES[patternStr] ) {
        const alias = Input.#PATTERN_ALIASES[patternStr];
        $input.attr('pattern', alias.native);
        negativeRegex = alias.negative;
        $input.data('negativeRegex', negativeRegex);
      }
      else {
        try {
          const match = patternStr.match(/^\[(.*)\]\+?$/);
          if ( match && match[1] ) {
            negativeRegex = new RegExp(`[^${match[1]}]`, 'g');
            $input.data('negativeRegex', negativeRegex);
          }
        }
        catch (e) {}
      }
    }

    if ( !negativeRegex ) return;

    const newVal = val.replace(negativeRegex, '');

    if ( val !== newVal ) {
      $input.value(newVal);

      const lang = root.lang || 'en';
      const defaultMsg =
        Input.#DEFAULT_ERROR_MSG[lang] || Input.#DEFAULT_ERROR_MSG['en'];
      const errMsg = $input.data('errorMsg') || defaultMsg;

      Input.setError($input, errMsg, 3000);
    }
    else Tooltip.remove();
  }

  // 🌟 [NEW] 단일 인풋 유효성 검사 코어 엔진 (재사용 목적)
  static validateSingleField(elem) {
    const $input = jQuery(elem);
    let isValid = true;
    let errorMsg = '';
    const lang = document.documentElement.lang || 'en';

    // 1. 필수 값 누락 검사 (required)
    if ( elem.validity.valueMissing ) {
      // 🌟 [핵심 방어선] required에 걸렸지만, 서버에서 프리로드된 파일 딱지가 있다면 유효한 것으로 판정!
      if ( $input.data('filePreload') ) isValid = true;
      else {
        isValid = false;
        errorMsg = $input.data('errorReq') || Input.#DEFAULT_REQ_MSG[lang] || Input.#DEFAULT_REQ_MSG['en'];
      }
    }
    // 2. 형식 어긋남 검사 (pattern, type)
    else if ( elem.validity.patternMismatch || elem.validity.typeMismatch ) {
      isValid = false;
      errorMsg = $input.data('errorMsgFormat') || $input.data('errorMsg') || Input.#DEFAULT_ERROR_MSG[lang] || 'Invalid format.';
    }
    // 3. 커스텀 일치 검사 (비밀번호 확인 등 data-match)
    else if ( $input.data('match') ) {
      const $target = jQuery($input.data('match'));
      if ( $target.be() && $target.value() !== $input.value() ) {
        isValid = false;
        errorMsg = $input.data('errorMsgMatch') || 'Does not match.';
      }
    }

    // 결과에 따라 툴팁 처리 전 혹시 이 인풋에 기존 툴팁 있다면 제거
    Tooltip.remove($input);
    if ( !isValid ) Input.setError($input, errorMsg);
    else Input.clearError($input);

    return isValid;
  }

  // 🌟 [수정됨] 통합 폼 유효성 검사 API (Submit 시점)
  static validateForm($form) {
    let isFormValid = true;
    let $firstInvalid = null;

    // 🌟 [핵심 추가] 버튼을 누르는 순간, 과거에 떠 있던 낡은 툴팁들을 화면에서 한 번 싹 밀어버리고 시작합니다!
    Tooltip.remove();

    $form.find(inputs.all).each((_, elem) => {
      // 위에서 만든 코어 엔진을 호출만 하면 끝!
      if ( !Input.validateSingleField(elem) ) {
        isFormValid = false;
        if ( !$firstInvalid ) $firstInvalid = jQuery(elem);
      }
    });

    // 🌟 [수정] 포커스 대신 질문자님이 만드신 강력한 smartScroll 확장 메서드 호출!
    // 툴팁이 먼저 렌더링된 후 화면이 스무스하게 이동하도록 약간의 delay(50ms)를 주면 UX가 극대화됩니다.
    if ( $firstInvalid ) {
      $firstInvalid.smartScroll({ block: 'center', delay: 50 });
    }

    return isFormValid;
  }

  // 🌟 [수정됨] 포커스 아웃 시 유효성 검사 (Blur 시점)
  #validateFormatOnBlur(event) {
    const elem = event.currentTarget;
    const $input = jQuery(elem);

    // 값이 없고 필수 입력(required)도 아니라면 에러 지우고 쿨하게 패스
    if ( !$input.value() && !$input.prop('required') ) {
      Input.clearError($input);
      return;
    }

    // 값이 있거나 필수 입력 항목이라면 코어 엔진에 검증을 맡김!
    Input.validateSingleField(elem);
  }

  #validateFile(file, $input) {
    const maxSizeMB = $input.data('fileMaxsize');
    const acceptStr = $input.attr('accept');

    // HTML에서 세분화된 에러 메시지를 각각 가져옴
    const msgSize = $input.data('errorMsgSize') || 'Max file size exceeded.';
    const msgType = $input.data('errorMsgType') || 'Unsupported file format.';

    // 1. 용량 체크
    if ( maxSizeMB ) {
      const maxBytes = parseFloat(maxSizeMB) * 1024 * 1024;
      if ( file.size > maxBytes ) {
        Input.setError($input, msgSize?.replace('{max}', maxSizeMB), 3000);
        return false;
      }
    }

    // 2. 확장자 체크
    if ( acceptStr ) {
      const fileName = file.name;
      const fileExt = '.' + fileName.split('.').pop().toLowerCase();
      const fileType = file.type;

      const acceptedTypes = acceptStr.split(',').map(s => s.trim().toLowerCase());

      const isValid = acceptedTypes.some(type => {
        if ( type.startsWith('.') ) return fileExt === type;
        if ( type.endsWith('/*') ) return fileType.startsWith(type.split('/')[0] + '/');
        return fileType === type;
      });

      if ( !isValid ) {
        Input.setError($input, msgType, 3000);
        return false;
      }
    }

    Tooltip.remove();
    return true;
  }

  // 1. 파일 핸들링 메서드 수정
  #handleFileChange($input) {
    if ( $input.type() !== 'file' ) return;

    const fileElem = $input.get(0);
    const file = fileElem.files && fileElem.files.length > 0 ? fileElem.files[0] : null;
    let $mockName = $input.siblings('[data-file-mock]');

    if ( file ) {
      // 🌟 [추가] 유저가 직접 새로운 파일을 선택하면, 프리로드 상태를 해제합니다.
      $input.removeDatattr('filePreload');

      if ( !this.#validateFile(file, $input) ) {
        $input.val('').removeDatattr('hasValue').blur();
        if ( $mockName.be() ) $mockName.remove();
        return;
      }
      const fileName = file.name;
      if ( !$mockName.be() ) {
        $mockName = jQuery('<span data-file-mock></span>').insertAfter($input);
      }
      $mockName.text(fileName);
    }
    else {
      // 🌟 [핵심 방어막] 파일이 비어있더라도, 서버에서 프리로드한 파일이 있다면 지우지 않고 통과!
      if ( $input.data('filePreload') ) {
        $input.datattr({'hasValue': ''});
        return;
      }

      if ( $mockName.be() ) $mockName.remove();
      Input.clearError($input);
    }
  }

  #handleIconAction(event) {
    event.preventDefault();
    event.stopPropagation();

    const $icon = jQuery(event.currentTarget);
    const action = $icon.data('iconAction'); // 'delete', 'search' 등
    const $input = $icon.siblings('input');

    if ( !$input.be() ) return;
    if ( $input.be() && $input.readonly() ) return;

    switch ( action ) {
      case 'delete': this.#actionDelete($input); break;
      case 'search': this.#actionSearch($input); break;
      case 'toggle': this.#actionToggle($input, $icon); break;
    }
  }

  // 액션 1: 삭제
  #actionDelete($input) {
    // 🌟 [순서 변경] 방어막 딱지를 '먼저' 제거합니다.
    $input.removeDatattr('filePreload');

    // 값을 비웁니다. (이 순간 트리거되는 handleFileChange가 위의 딱지 제거를 인지하고 mock을 지웁니다)
    $input.value('');

    if ( $input.type() === 'file' ) $input.blur();
    else $input.focus();
  }

  // 액션 2: 검색 (데이터만 던져주고 역할 끝!)
  #actionSearch($input) {
    const keyword = $input.value();
    if ( !keyword ) {
      $input.focus();
      return;
    }
    $input.trigger('action:search', { keyword: keyword });
  }

  // 🌟 액션 3: 비밀번호 보기/숨기기 토글
  #actionToggle($input, $icon) {
    const isPassword = $input.type() === 'password';
    const iconNames = $icon.data('iconToggle').split(',').map(s => s.trim());

    $icon.text(isPassword ? iconNames[0] : iconNames[1]);
    $input.type(isPassword ? 'text' : 'password').focus();
  }

  // 🌟 [추가] 인풋 엔터키 감지 (다이얼로그 닫힘 방지 & 검색 트리거)
  #handleEnterKey(event) {
    if ( event.key !== 'Enter' ) return;
    event.preventDefault();
    const $input = jQuery(event.currentTarget);
    const $searchIcon = $input.siblings('[data-icon-action="search"]');
    if ( $searchIcon.be() ) this.#actionSearch($input);
  }

  // 🌟 [NEW] 카드 번호 자동 포맷팅 (아멕스 동적 인식)
  #formatCardNumber(event) {
    const $input = jQuery(event.currentTarget);
    let val = $input.value().replace(/\D/g, ''); // 숫자 이외의 문자 제거
    let formatted = '';

    // 아멕스(AMEX) 카드 판별: 34 또는 37로 시작
    const isAmex = /^3[47]/.test(val);

    if ( isAmex ) {
      // AMEX 포맷: 4 - 6 - 5
      const p1 = val.substring(0, 4);
      const p2 = val.substring(4, 10);
      const p3 = val.substring(10, 15);

      formatted = p1;
      if ( val.length > 4 ) formatted += ' ' + p2;
      if ( val.length > 10 ) formatted += ' ' + p3;
    }
    else {
      // 일반 카드 포맷: 4 - 4 - 4 - 4
      formatted = val.match(/.{1,4}/g)?.join(' ') || '';
    }

    if ( $input.value() !== formatted ) $input.value(formatted);
  }

  // 🌟 [NEW] 카드 유효 기간 자동 포맷팅 (MM / YY)
  #formatCardExpiry(event) {
    const $input = jQuery(event.currentTarget);
    let val = $input.value().replace(/\D/g, ''); // 숫자 이외 제거
    let formatted = val;

    // 첫 자리가 2~9인 경우 자동으로 '0'을 붙여줌 (스마트 패딩)
    if ( val.length === 1 && parseInt(val, 10) > 1 ) formatted = '0' + val;

    // 2자리가 넘어가면 ' / ' 구분자를 삽입하고 최대 4자리 숫자(YY)까지만 허용
    if ( formatted.length > 2 ) {
      formatted
        = formatted.substring(0, 2)+ ' / ' + formatted.substring(2, 4);
    }

    if ( $input.value() !== formatted ) $input.value(formatted);
  }

  watch() {
    // 1. 기존 정적 DOM 요소 초기화 (로드 시점)
    jQuery(this.selectors).each((_, elem) => this.#init(jQuery(elem)));

    // 🌟 2. [핵심] 동적으로 추가된 DOM(Dynamic elements)을 위한 상호작용 이벤트 위임
    // 사용자가 동적 생성 인풋을 클릭(focusin)하거나 값 변경(change) 최초의 순간,
    // 자동으로 #init()을 실행, pattern 속성을 브라우저 표준 정규식으로 즉시 치환합니다.
    $doc.on('focusin change', this.selectors, (event) =>
      this.#init(jQuery(event.currentTarget))
    );

    // 🌟 [추가] 타이핑(input)하는 즉시 값이 있는지 없는지 판단해서 아이콘을 보여줌!
    $doc.on('input', this.selectors, (event) => {
      const $input = jQuery(event.currentTarget);
      // 1. 값 존재 여부에 따른 아이콘 표시 토글
      $input.toggleDatattrIf('hasValue', !!$input.value());
      // 🌟 2. 사용자가 타이핑을 시작하면 (수정 의지가 있으므로) 기존 에러 상태 즉시 해제!
      Input.clearError($input);
    });

    $doc.on('focusin input keydown paste cut drop beforeinput compositionstart', '[data-prevent-edit], [data-datetimer], [data-address-dialog]', (event) =>
      this.#handlePreventEdit(event)
    );

    $doc.on('keydown', '[data-prevent-space]', (event) =>
      this.#handlePreventSpaceKeydown(event)
    );
    $doc.on('input', '[data-prevent-space]', (event) =>
      this.#handlePreventSpaceInput(event)
    );

    $doc.on('propchange', this.selectors, (event, data) =>
      this.#handleDisabledChange(event, data)
    );

    $doc.on('propchange', 'optgroup, option', (event, data) => {
      if ( data && data.prop === 'disabled' ) {
        const $select = jQuery(event.currentTarget).closest('select');
        if ( $select.be() ) this.#checkSelectDisabledState($select);
      }
    });

    $doc.on('input compositionend', 'input[pattern]', (event) => {
      this.#filterPattern(event);
    });

    // 🟢 (추가) 인풋창 엔터(Enter) 키 감지
    $doc.on('keydown', 'input', (event) => this.#handleEnterKey(event));

    // 이메일 및 비밀번호 검증 트리거 (blur)
    $doc.on('blur', 'input[type="password"], input[name*="password"], input[id*="password"], input[data-password], input[data-password-confirm],  input[type="email"]', (event) =>
      this.#validateFormatOnBlur(event)
    );

    // 🌟 (추가) 원본 인풋의 값이 변경(blur)될 때, 나를 참조하고 있는 매칭 필드가 있다면 동반 유효성 검사 트리거!
    $doc.on('blur', 'input', (event) => {
      const id = event.currentTarget.id;
      if ( !id ) return;

      // 현재 수정된 인풋의 ID를 data-match 속성으로 가지고 있는 요소들 찾기
      const $matchers = jQuery(`input[data-match="#${id}"]`);
      $matchers.each((_, elem) => {
        // 매칭 필드에 이미 입력된 값이 있을 때만 재검사 실행
        if ( jQuery(elem).value() ) {
          Input.validateSingleField(elem);
        }
      });
    });

    // 🌟 [추가] 폼 리셋(취소/원복) 시 모든 에러 상태 및 툴팁 완벽 초기화
    $doc.on('reset', 'form', (event) => {
      const $form = jQuery(event.currentTarget);

      // 폼 내부에 있는 모든 인풋들을 순회하며 에러 상태를 깨끗하게 지웁니다.
      $form.find(this.selectors).each((_, elem) => {
        Input.clearError(jQuery(elem));
      });

      // 혹시라도 남아있는 툴팁 DOM이 있다면 확실하게 날려버립니다.
      if (typeof Tooltip !== 'undefined') Tooltip.remove();
    });

    // 🌟 [NEW] 카드 번호 포맷팅 이벤트 바인딩
    $doc.on('input', 'input[data-card-number]', (event) => {
      this.#formatCardNumber(event);
    });

    // 🌟 [NEW] 카드 유효 기간 포맷팅 이벤트 바인딩
    $doc.on('input', 'input[data-card-expiry]', (event) => {
      this.#formatCardExpiry(event);
    });
  }
}


export class Autocomplete {
  // 🌟 [복구 1] 여러 JSON 파일을 캐싱할 수 있도록 다시 객체 형태로 복구
  static #cache = {};

  constructor(selectors = '[data-autocomplete]') {
    this.selectors = selectors;
  }

  // 🌟 [복구 2] 파일명을 인자로 받아 동적으로 fetch (범용성 복구)
  // async #fetchData(filename) {
  //   if ( Autocomplete.#cache[filename] ) return Autocomplete.#cache[filename];

  //   try {
  //     const response = await fetch(`/js/data/${filename}.json`);
  //     if ( !response.ok ) throw new Error();

  //     const data = await response.json();
  //     Autocomplete.#cache[filename] = data;
  //     return data;
  //   }
  //   catch (error) {
  //     Dialog.error(`Autocomplete failed to load ${filename}.json.`);
  //     return [];
  //   }
  // }
  async #fetchData(filename) {
    if ( Autocomplete.#cache[filename] ) return Autocomplete.#cache[filename];

    try {
      // 🌟 여기에 { cache: 'no-cache' } 옵션을 추가합니다.
      // (서버에 변경 사항이 있는지 확인 후, 변경되었을 때만 새로 다운로드합니다.)
      const response = await fetch(`/js/data/${filename}.json`, { cache: 'no-cache' });

      if ( !response.ok ) throw new Error();

      const data = await response.json();
      Autocomplete.#cache[filename] = data;
      return data;
    }
    catch (error) {
      Dialog.error(`Autocomplete failed to load ${filename}.json.`);
      return [];
    }
  }

  async #handleInput(event) {
    const $input = jQuery(event.currentTarget);

    if ( $input.readonly() ) return;

    const type = $input.data('autocomplete');
    const keyword = $input.value();
    const lowerKeyword = keyword.toLowerCase();
    const currentLang = root.lang || 'ko';

    let $list = $input.siblings('.autocomplete-list');

    if ( $list.be('not') ) {
      $list = jQuery('<div class="autocomplete-list"></div>').insertAfter($input);
    }

    if ( !keyword ) {
      $list.empty().hide();
      $input.removeDatattr('autocompleteSelected preventEdit');
      return;
    }

    if ( $input.data('preventEdit') !== undefined ) return;
    if ( $input.data('autocompleteSelected') && event.type === 'change' ) return;

    // 🌟 [수정] 강제 트리거 이벤트가 아닌, 진짜 유저 타이핑(originalEvent) 때만 삭제!
    if ( event.type === 'input' && event.originalEvent ) {
      $input.removeDatattr('autocompleteSelected');
    }

    // 🌟 [핵심] type에 따라 불러올 JSON 파일명(filename)을 스마트하게 매핑!
    const isIsoGroup = ['country', 'country-idd', 'idd'].includes(type);
    const filename = isIsoGroup ? 'iso' : type; // iata는 'iata.json'을 부름

    const source = await this.#fetchData(filename);

    const filtered = source.filter(item =>
      item.keywords && item.keywords.includes(lowerKeyword)
    );

    if ( filtered.length > 0 ) {
      filtered.sort((a, b) => {
        const nameA = a.names?.[currentLang] || a.names?.en || '';
        const nameB = b.names?.[currentLang] || b.names?.en || '';
        return nameA.localeCompare(nameB, currentLang);
      });

      const safeKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${safeKeyword})`, 'gi');

      const html = filtered.map(item => {
        const enName = item.names?.en || '';
        const localName = item.names?.[currentLang] || '';

        const useOnlyEn = !localName || currentLang === 'en' || localName.toLowerCase() === enName.toLowerCase();
        const displayName = useOnlyEn ? enName : localName;

        let dynamicLabel = '';
        let dataAttrs = '';

        // 🌟 [분기 처리] ISO 그룹일 때와 일반 그룹(IATA 등)일 때의 데이터 렌더링 분리
        if ( isIsoGroup ) {
          if ( type === 'idd' ) {
            dynamicLabel = displayName ? `${item.idd} (${displayName})` : item.idd;
          } else {
            dynamicLabel = displayName || item.country;
          }
          // 공통 value에는 기준점인 ISO 국가 코드를 넣고, IDD 연동을 위한 데이터를 추가로 매달아둠
          dataAttrs = `data-value="${item.country}" data-idd="${item.idd}" data-display="${displayName}"`;
        }
        else { // IATA 등 기타 타입
          dynamicLabel = displayName ? `${displayName} (${item.value})` : item.value;
          // 일반 타입은 심플하게 value 속성만 가짐
          dataAttrs = `data-value="${item.value}"`;
        }

        const highlightedLabel = dynamicLabel.replace(regex, '<strong>$1</strong>');
        return `<div class="autocomplete-item" ${dataAttrs}>${highlightedLabel}</div>`;
      }).join('');

      $list.html(html).show();
    }
    else {
      $list.empty().hide();
    }
  }

  async #applySelection($item, $input, $list) {
    // 🌟 data-value는 ISO일 땐 국가코드(KR), IATA일 땐 항공사코드(UO)가 자동으로 들어옵니다.
    const selectedValue = $item.data('value');
    const selectedLabel = $item.text();
    const type = $input.data('autocomplete');

    $input.datattr({ autocompleteSelected: selectedValue, preventEdit: '' }).value(selectedLabel);
    $list.empty().hide();

    // 🌟 'country-idd' 연동 기능은 ISO 데이터 구조(data-idd)가 있을 때만 작동
    if ( type === 'country-idd' ) {
      const $iddInput = jQuery('input[data-autocomplete="idd"]');

      if ( $iddInput.be() ) {
        const selectedIdd = $item.data('idd');
        const selectedDisplay = $item.data('display');
        const iddLabel = selectedDisplay ? `${selectedIdd} (${selectedDisplay})` : selectedIdd;

        $iddInput.datattr({ autocompleteSelected: selectedValue, preventEdit: '' }).value(iddLabel);
      }
    }

    const nextFocus = $input.data('autocompleteNextFocus');
    if ( nextFocus ) {
      const $target = jQuery(nextFocus);
      if ( $target.be() ) {
        $target.focus();
        return;
      }
    }

    $input.blur();
  }

  #handleSelect(event) {
    event.preventDefault();
    const $item = jQuery(event.currentTarget);
    const $list = $item.closest('.autocomplete-list');
    const $input = $list.siblings(this.selectors);

    this.#applySelection($item, $input, $list);
  }

  #handleFocusOut(event) {
    const $input = jQuery(event.currentTarget);
    const $list = $input.siblings('.autocomplete-list');

    if ( !$input.data('autocompleteSelected') ) $input.value('');
    if ( $list.be() ) $list.empty().hide();
  }

  #handleKeydown(event) {
    const allowedKeys = ['ArrowDown', 'ArrowUp', 'Enter', 'Escape'];
    if ( !allowedKeys.includes(event.key) ) return;

    const $input = jQuery(event.currentTarget);

    if ( $input.readonly() ) return;

    const $list = $input.siblings('.autocomplete-list');
    if ( !$list.is(':visible') ) return;

    const $items = $list.find('.autocomplete-item');

    if ( $items.be('not') ) return;

    let currentIndex = $items.index($items.filter('.is-active'));

    switch ( event.key ) {
      case 'ArrowDown':
        event.preventDefault();
        currentIndex = (currentIndex + 1) % $items.length;
        this.#highlightItem($items, currentIndex, $list);
        break;

      case 'ArrowUp':
        event.preventDefault();
        currentIndex = currentIndex <= 0 ? $items.length - 1 : currentIndex - 1;
        this.#highlightItem($items, currentIndex, $list);
        break;

      case 'Enter':
        event.preventDefault();
        if ( currentIndex > -1 ) {
          this.#applySelection($items.eq(currentIndex), $input, $list);
        }
        break;

      case 'Escape':
        event.preventDefault();
        $list.empty().hide();
        break;
    }
  }

  #highlightItem($items, index, $list) {
    $items.removeClass('is-active');
    const $active = $items.eq(index).addClass('is-active');

    const listEl = $list.get(0);
    const activeEl = $active.get(0);

    const offsetBottom = activeEl.offsetTop + activeEl.offsetHeight;
    const scrollBottom = listEl.scrollTop + listEl.offsetHeight;

    if ( offsetBottom > scrollBottom ) {
      listEl.scrollTop = offsetBottom - listEl.offsetHeight;
    } else if ( activeEl.offsetTop < listEl.scrollTop ) {
      listEl.scrollTop = activeEl.offsetTop;
    }
  }

  watch() {
    $doc.on('input change', this.selectors, (event) => this.#handleInput(event));
    $doc.on('keydown', this.selectors, (event) => this.#handleKeydown(event));
    $doc.on('mousedown', '.autocomplete-item', (event) => this.#handleSelect(event));
    $doc.on('focusout', this.selectors, (event) => this.#handleFocusOut(event));
  }
}


export class Datetimer {
  constructor(selector = '[data-datetimer]') {
    this.selector = selector;
    this.layerClass = 'datetimer';
  }

  // AJAX 데이터 페칭 헬퍼 (dir 파라미터 추가)
  async #fetchData($input, targetDate, dir = 1) {
    try {
      const html = await jQuery.ajax({
        url: '/js/ajax/datetimer.php',
        method: 'GET',
        data: {
          type: $input.data('datetimer'),
          dateSelected: $input.data('datetimerSelected') || '',
          target: targetDate,
          from: $input.data('datetimerFrom') || '',
          till: $input.data('datetimerTill') || '',
          disabled: $input.data('datetimerDisabled') || '',
          disabledDow: $input.data('datetimerDisabledDow') || '',
          stepM5: $input.data('datetimerStepM5') || '',
          dir: dir // [NEW] 이동 방향 데이터 전송 (-1 또는 1)
        }
      });
      return jQuery(jQuery.parseHTML(html));
    }
    catch (error) {
      // [수정] 다이얼로그가 열리기 전에 인풋 포커스 강제 해제 (무한 루프 방지)
      $input.blur();
      Dialog.error(`Datetimer template load failed: ${error.status}`);
      return null;
    }
  }

  // 슬라이드 네비게이션 처리
  async #navigate($input, targetDate, isJump = false, dir = 1) {
    const $layer = jQuery(`#${$input.data('datetimerLayerId')}`);

    if ( $layer.hasClass('is-animating') ) return;
    $layer.addClass('is-animating');

    const $parsed = await this.#fetchData($input, targetDate, dir);
    if ( !$parsed ) {
      $layer.removeClass('is-animating');
      return;
    }

    const currentMonth = $layer.find('.calendar-nav').data('currentTarget');
    const $wrapper = $layer.find('.calendar-wrapper');

    $layer.find('.calendar-nav').replaceWith(
      $parsed.filter('.calendar-nav')
    );

    if ( isJump ) {
      $wrapper.html($parsed.filter('.calendar-wrapper').html());
      $layer.removeClass('is-animating');
      return;
    }

    const newMonth = $parsed.filter('.calendar-nav').data('currentTarget');
    const posClass = newMonth < currentMonth ? 'slide-left' : 'slide-right';

    // 1. 새 달력을 화면 밖에 배치
    const $newGrid = $parsed.filter('.calendar-wrapper').find('> .calendar')
      .removeClass('slide-active').addClass(posClass);

    // * [jQuery Animate 1] 애니메이션이 튀지 않도록 현재 높이 고정
    const startHeight = $wrapper.height();
    $wrapper.height(startHeight);

    $wrapper.append($newGrid);
    $wrapper[0].offsetHeight; // 강제 리플로우 (위치 인식)

    // 2. CSS 클래스 토글을 통한 좌우 슬라이드 (GPU 가속)
    const $oldGrid = $wrapper
      .find(`> .calendar[data-calendar-month="${currentMonth}"]`);
    $newGrid.removeClass('slide-left slide-right').addClass('slide-active');
    $oldGrid.removeClass('slide-active').addClass(
      posClass === 'slide-right' ? 'slide-left' : 'slide-right'
    );

    // * [jQuery Animate 2] 새 달력의 높이로 부드럽게 애니메이션!
    const targetHeight = $newGrid.height();
    $wrapper.animate({ height: targetHeight }, 400, 'swing', () => {
      // 애니메이션이 끝나면 인라인 height를 지워서 다시 반응형(auto)으로 복구
      $wrapper.css('height', '');
    });

    // 3. 슬라이드 종료(400ms) 후 구형 달력 삭제 및 락 해제
    setTimeout(() => {
      $oldGrid.remove();
      $layer.removeClass('is-animating');
    }, 400);
  }

  // 최초 오픈
  async #openLayer(event) {
    const $input = jQuery(event.currentTarget);
    if ( !$input?.be() || $input.data('datetimerLayerId') ) return;

    // [수정] 커스텀 익스텐션 .disabled(), .readonly() 활용
    if ( !$input?.be() || $input.data('datetimerLayerId') || $input.disabled() || $input.readonly() ) return;

    // 싱글톤 보장: 다른 인풋의 달력이 열려 있다면 무조건 닫기
    this.#closeAllLayers();

    const shortId = Math.random().toString(36).substring(2, 7);
    const layerID = `datetimer-${shortId}`;

    // 🌟 [NEW] 타입에 따른 추가 클래스 결정
    const type = $input.data('datetimer'); // 'date' 또는 'time'
    const typeClass = type ? ` datetimer-${type}` : '';

    $input.datattr({ datetimerLayerId: layerID, datetimerActive: true });
    $input.css('anchor-name', `--anchor-${shortId}`);

    // 1. 기본 래퍼 추가 (기본 닻: left)
    const wrapperHtml = `<div id="${layerID}" class="${this.layerClass}${typeClass}" style="position: absolute; position-anchor: --anchor-${shortId}; inset-block-start: anchor(bottom); inset-inline-start: anchor(left); margin-block-start: 0.5rem; z-index: var(--z-datetimer);"></div>`;
    $body.fadeInAppend(wrapperHtml);

    // 2. AJAX 데이터 페칭 및 달력 렌더링
    const targetDate = $input.data('datetimerTarget') || '';
    const $parsed = await this.#fetchData($input, targetDate);

    // [수정] 에러 등으로 데이터를 못 가져왔을 경우
    if ( !$parsed ) {
      this.#closeLayer($input); // 만들어둔 래퍼 DOM과 속성을 즉시 삭제
      return; // 렌더링 중지
    }

    const $layer = jQuery(`#${layerID}`);
    $layer.html($parsed);

    // 3. [NEW] 화면 이탈 감지 및 위치 재조정 (Dynamic Alignment)
    if ( $layer.be() ) {
      // 렌더링된 레이어의 실제 좌표 측정
      const rect = $layer[0].getBoundingClientRect();
      const vw = window.innerWidth;
      const safeSpace = 5; // 우측 안전 여백 (15px)

      // 레이어의 우측 끝(rect.right)이 뷰포트 너비를 넘어가면 방향 반전
      if ( rect.right > vw - safeSpace ) {
        $layer.css({
          'inset-inline-start': 'auto',        // 기존 left 닻 해제
          'inset-inline-end': 'anchor(right)'  // right 닻으로 변경하여 우측 정렬
        });
      }
    }
  }

  // 특정 레이어 닫기
  #closeLayer($input) {
    const layerID = $input?.data('datetimerLayerId');
    if ( !layerID ) return;

    const $layer = jQuery(`#${layerID}`);
    if ( $layer.be() ) {
      $layer.fadeOutRemove(400, () => {
        $input.removeDatattr('datetimerLayerId');
      });
      $input.removeDatattr('datetimerActive');
    }
  }

  // 모든 레이어 닫기
  #closeAllLayers() {
    jQuery(`.${this.layerClass}`).fadeOutRemove();
    jQuery(this.selector).removeDatattr('datetimerLayerId datetimerActive');
  }

  // 외부 영역 클릭 처리
  #handleOutsideInteraction(event) {
    const $target = jQuery(event.target);

    // click 이벤트 시, 클릭 직후 DOM에서 사라진 요소(예: 리렌더링) 무시
    if ( event.type === 'click' && !document.contains(event.target) ) return;
    if ( jQuery(`.${this.layerClass}.is-animating`).be() ) return;

    // [수정] select, option 무시 삭제. 대신 타겟이 인풋이나 달력 내부인지 정확히 판별
    if ( !$target.closest(this.selector).be() && !$target.closest(`.${this.layerClass}`).be() ) {
      this.#closeAllLayers();
    }
  }

  // 버튼 내비게이션 클릭 처리
  #navCalendar(event) {
    const $btn = jQuery(event.currentTarget);
    const layerID = $btn.closest(`.${this.layerClass}`).id();
    const $input = jQuery(this.selector).filter((_, el) => jQuery(el).data('datetimerLayerId') === layerID);

    const targetDate = $btn.attr('data-nav-calendar');

    // [NEW] 타겟 날짜와 현재 뷰의 날짜를 비교해 이동 방향 계산 (-1: 과거, 1: 미래)
    const currentTarget = $btn.closest('.calendar-nav').data('currentTarget');
    const dir = (targetDate < currentTarget) ? -1 : 1;

    this.#navigate($input, targetDate, false, dir);
  }

  // 셀렉트 박스 점프 처리
  #jumpCalendar(event) {
    const $layer = jQuery(event.currentTarget)
      .closest(`.${this.layerClass}`);
    const $input = jQuery(this.selector).filter((_, el) =>
      jQuery(el).data('datetimerLayerId') === $layer.id()
    );

    const year = $layer.find('[data-nav-year]').value();
    const month = $layer.find('[data-nav-month]').value();
    const targetDate = `${year}-${String(month).padStart(2, '0')}-01`;

    // [NEW] 점프 시에도 타겟 날짜에 따른 방향 계산
    const currentTarget = $layer.find('.calendar-nav').data('currentTarget');
    const dir = (targetDate < currentTarget) ? -1 : 1;

    this.#navigate($input, targetDate, true, dir);
  }

  // 날짜/시간 선택 처리
  #pick(event) {
    event.preventDefault(); // 혹시 모를 기본 동작 방지

    const $cell = jQuery(event.currentTarget);
    if ( $cell.hasClass('disabled') || $cell.hasClass('selected') ) return;

    const layerID = $cell.closest(`.${this.layerClass}`).id();
    const $input = jQuery(this.selector).filter((_, el) =>
      jQuery(el).data('datetimerLayerId') === layerID
    );

    if ( !$input.be() ) return;

    const $time = $cell.find('time');
    const datetime = $time.datetime();

    if ( datetime ) {
      $input.datattr({ datetimerSelected: datetime })
        .value($time.data('datetimeText'))
        .trigger('change'); // 🌟 [추가] 값 변경 이벤트를 강제로 태워서 다른 로직들이 인지하게 함

      // 🌟 [추가] 값이 정상적으로 선택되었으므로 에러 툴팁과 빨간 줄 즉시 강제 삭제
      if ( typeof Input !== 'undefined' ) Input.clearError($input);

      this.#closeLayer($input);
    }
  }

  // * 깔끔하게 정리된 Watcher (이벤트 매핑)
  watch() {
    // [수정] click뿐만 아니라 focusin(Tab 키 포커스) 시에도 달력 오픈
    $doc.on('click focusin', this.selector, (event) =>
      this.#openLayer(event)
    );
    $doc.on('click focusin', (event) =>
      this.#handleOutsideInteraction(event)
    );
    $doc.on('click', `.${this.layerClass} [data-nav-calendar]`, (event) =>
      this.#navCalendar(event)
    );
    $doc.on('change', `.${this.layerClass} select`, (event) =>
      this.#jumpCalendar(event)
    );
    // [수정] .day 뿐만 아니라 .time-cell 도 동일하게 픽킹 이벤트 처리
    $doc.on('click', `.${this.layerClass} .day, .${this.layerClass} .time`, (event) =>
      this.#pick(event)
    );
  }
}


export class Toast {
  // 🌟 성공/실패 구분 없이 메시지만 전달받아 화면에 즉시 표시하는 단일 오픈 정적 메서드
  static open(message, duration = 3000) {
    if ( !message ) return;

    let $containers = jQuery('.toast-container');

    // 최상위 컨테이너가 화면에 없을 경우에만 최초 1회 생성 및 body에 부착
    if ( $containers.length === 0 ) $containers = jQuery('<div class="toast-container"></div>').appendTo('body');

    // 💡 [보안 최적화] 외부 메시지 주입 시 발생할 수 있는 XSS 공격 방지를 위해 text() 공정을 거칩니다.
    const $toastElements = jQuery('<div class="toast"></div>').text(message);

    // 🌟 [핵심 수정] append 대신 prepend를 사용하여 새 토스트를 무조건 맨 아래(첫 번째)에 주입합니다!
    $containers.prepend($toastElements);

    // CSS 트랜지션이 안전하게 먹히도록 렌더링 큐에 아주 미세한 시간 차를 두고 등장 클래스 부여
    setTimeout(() => $toastElements.addClass('is-show'), 10);

    // 지정된 노출 시간이 지나면 자동으로 사라지고 DOM 트리에서 깔끔하게 제거
    setTimeout(() => {
      $toastElements.removeClass('is-show');

      setTimeout(() => $toastElements.remove(), 300);
    }, duration);
  }
}


export class Dialog {
  static #templates = null;

  // 1. 내부 헬퍼: 템플릿 로드 및 캐싱
  static async #init() {
    if ( this.#templates ) return;

    try {
      this.#templates = await jQuery.ajax({
        url: '/js/ajax/dialog.php',
        method: 'GET'
      });
    }
    catch (error) {
      console.error('Failed to load dialog templates:', error);
    }
  }

  // 2. 내부 헬퍼: 다이얼로그 닫기 및 DOM 정리 전담
  static #close($dialog, dialog, resolve) {
    Scroll.unlock();
    resolve(dialog.returnValue);

    if ( document.activeElement ) document.activeElement.blur();

    const removeDialog = () => {
      if ( $dialog.parent().length ) $dialog.remove();
    };

    $dialog.on('transitionend', (event) => {
      if ( event.target === dialog ) removeDialog();
    });

    setTimeout(removeDialog, 550);
  }

  // 3. 내부 헬퍼: 다이얼로그 생성 및 세팅 전담
  static async #open(type, message, options) {
    await this.#init();

    return new Promise((resolve) => {
      if ( !this.#templates ) {
        resolve(null);
        return;
      }

      const $dialog = jQuery(this.#templates).filter(`dialog[data-dialog="${type}"]`).clone();

      if ( $dialog.be(false) ) {
        resolve(null);
        return;
      }

      $body.append($dialog);
      const dialog = $dialog.get(0);

      if ( options.title ) $dialog.find('[data-title]').text(options.title);
      if ( message ) $dialog.find('[data-message]').html(message);

      // 🌟 [추가] 옵션에 커스텀 확인 버튼 텍스트가 있다면, value="confirm"인 버튼을 찾아 텍스트를 변경합니다.
      if ( options.confirmBtnText ) {
        $dialog.find('button[value="confirm"]').text(options.confirmBtnText);
      }
      if ( options.confirmBtnClass ) {
        $dialog.find('button[value="confirm"]').addClass(options.confirmBtnClass);
      }
      if ( options.cancelBtnText ) {
        $dialog.find('button[value="cancel"]').text(options.cancelBtnText);
      }

      $dialog.one('close', () => this.#close($dialog, dialog, resolve));

      Scroll.lock($dialog);
      dialog.showModal();

      const $autoFocusElem = $dialog.find('[autofocus]');
      if ( $autoFocusElem.be() ) $autoFocusElem.get(0).focus();
      // else if ( document.activeElement ) document.activeElement.blur();
    });
  }

  // 🌟 [추가] HTML 속성을 통한 다이얼로그 강제 오픈 감지기
  static watch() {
    jQuery(document).on('click', '[data-dialog-open]', (event) => {
      event.preventDefault();

      const type = jQuery(event.currentTarget).data('dialogOpen');

      if ( type ) this.#open(type, '', {});
    });
  }

  // 🌟 [수정] 세 번째 파라미터로 confirmBtnText 추가 및 options에 전달
  static confirm(
    message, title = '',
    confirmBtnText = '', confirmBtnClass = '', cancelBtnText = ''
  ) {
    return this.#open(
      'confirm', message,
      { title, confirmBtnText, confirmBtnClass, cancelBtnText }
    );
  }

  // 🌟 [수정] 두 번째 파라미터로 confirmBtnText 추가 및 options에 전달
  static confirmSimple(
    message, confirmBtnText = '', confirmBtnClass = '', cancelBtnText = ''
  ) {
    return this.#open(
      'confirm-simple', message,
      { confirmBtnText, confirmBtnClass, cancelBtnText }
    );
  }

  // 4. 퍼블릭 정적 메서드
  static error(message, title = '') {
    return this.#open('error', message, { title });
  }
  static errorSimple(message) {
    return this.#open('error-simple', message, { });
  }

  static alert(message, title) {
    return this.#open('alert', message, { title });
  }
  static alertSimple(message) {
    return this.#open('alert-simple', message, { });
  }

  static address(options = {}) {
    return this.#open('address', '', options);
  }

  static legal(contents, title) {
    return this.#open('legal', contents, { title });
  }
}


export class DialogAddress {
  // 🌟 [수정] 다국어 우편번호 포맷 함수 추가
  static #MESSAGES = {
    'ko': {
      loading: '검색 중...',
      empty: '검색 결과가 없습니다.',
      error: '서버 에러가 발생했습니다.',
      zipFormat: (zip) => `(ZIP: ${zip})` // `(우 : ${zip})`
    },
    'en': {
      loading: 'Searching...',
      empty: 'No search results.',
      error: 'A server error occurred.',
      zipFormat: (zip) => `(ZIP : ${zip})`
    },
    'ja': {
      loading: '検索中...',
      empty: '検索結果がありません。',
      error: 'サーバーエラーが発生しました。',
      zipFormat: (zip) => `(ZIP : ${zip})` // `(〒 ${zip})`
    },
    'fr': { // 🌟 불어 추가
      loading: 'Recherche...',
      empty: 'Aucun résultat trouvé.',
      error: 'Une erreur de serveur est survenue.',
      zipFormat: (zip) => `(ZIP : ${zip})`  // `(Code postal : ${zip})`
    }
  };

  constructor(selectors = '[data-address-dialog]') {
    this.selectors = selectors;
    this.APIKEY = window.APIKEY_JUSO ?? '';
  }

  async #handleSearchTrigger(event) {
    const $input = jQuery(event.currentTarget);

    event.preventDefault();
    if ( document.activeElement === $input.get(0) ) $input.get(0).blur();

    if ( $input.data('isSearching') ) return;
    $input.datattr({ isSearching: true });

    const result = await Dialog.address();

    if ( result && result !== 'cancel' && result !== 'close' ) {
      try {
        const data = JSON.parse(result);
        // 1. 값 세팅
        // ? - 값 세팅 시 preventEditValue 함께 동기화 submit 시 롤백 버그 원천 차단
        jQuery('[data-address-dialog="zip"]')
          .value(data.zip).data('preventEditValue', data.zip);
        jQuery('[data-address-dialog="latin"]')
          .value(data.addr).data('preventEditValue', data.addr);

        // 🌟 [추가] 2. 지정된 타겟으로 포커스 이동
        const nextFocus = $input.data('addressDialogNextFocus');
        if ( nextFocus ) {
          const $target = jQuery(nextFocus);
          if ( $target.be() ) $target.focus();
        }
      }
      catch(e) {
        Dialog.error(`Data data parse failed: ${e}`);
      }
    }

    $input.removeDatattr('isSearching');
  }

  async #searchAddressAPI(event, data) {
    const $searchInput = jQuery(event.target);

    if ( $searchInput.name() !== 'address-keyword' ) return;

    const keyword = data.keyword;
    const $dialog = $searchInput.closest('dialog');
    const dialogNode = $dialog.get(0);
    const $results = $dialog.find('[data-address-results]');

    const currentLang = root.lang || 'en';
    const msg = DialogAddress.#MESSAGES[currentLang] || DialogAddress.#MESSAGES['en'];

    $results.css('display', 'flex').html(`<p class="loading">${msg.loading}</p>`);

    try {
      const response = await jQuery.ajax({
        url: 'https://business.juso.go.kr/addrlink/addrEngApi.do',
        method: 'GET',
        dataType: 'json',
        data: {
          confmKey: this.APIKEY,
          currentPage: 1,
          countPerPage: 20,
          keyword: keyword,
          resultType: 'json'
        }
      });

      const resData = response.results;

      if ( resData.common.errorCode !== "0" ) {
        $results.html(`<p class="error">${resData.common.errorMessage}</p>`);
        return;
      }

      if ( !resData.juso || resData.juso.length === 0 ) {
        $results.html(`<p class="error">${msg.empty}</p>`);
        return;
      }

      // 🌟 [수정] <address> 대신 웹 표준에 맞는 <button> 사용 및 우편번호 다국어 포맷 적용
      const html = resData.juso.map(item => `
        <button type="button" class="address-item" data-address-latin="${item.roadAddr}" data-address-zip="${item.zipNo}">
          <span class="address-local">${item.korAddr}</span>
          <span class="address-latin">${item.roadAddr} <span class="address-zip">${msg.zipFormat(item.zipNo)}</span></span>
        </button>
      `).join('');

      $results.html(html);

      // 🌟 [수정] jQuery data() 카멜케이스 문법에 맞게 키 이름 변경
      $results.find('.address-item').off('click').on('click', function() {
        const selectedData = {
          zip: jQuery(this).data('addressZip'),
          addr: jQuery(this).data('addressLatin')
        };
        dialogNode.close(JSON.stringify(selectedData));
      });

    } catch (error) {
      $results.html(`<p class="error">${msg.error}</p>`);
    }
  }

  watch() {
    $doc.on('click focusin', this.selectors, (event) =>
      this.#handleSearchTrigger(event)
    );

    $doc.on('action:search', 'input[name="address-keyword"]', (event, data) =>
      this.#searchAddressAPI(event, data)
    );
  }
}


export class Choice { // * CSS required
  static #aniAttr = 'is-rippling';

  constructor(selector) { this.selector = selector; } // checkbox, radio

  watch() {
    // ? 'change.choice' 네임스페이스 사용해 추후 특정 이벤트만 off() 가능
    $doc.on('change.choice', this.selector, (event) => {
      const $input = jQuery(event.currentTarget);
      if ( !$input.checked() ) return; // 체크 해제 시 애니메이션 불필요

      // * 핵심 : 애니메이션 종료 후 속성 제거 및 이벤트 리스너 정리
      $input.closest('label').datattr({ [Choice.#aniAttr]: '' })
        .on('animationend', function() {
          jQuery(this).removeDatattr(Choice.#aniAttr).off('animationend');
        });
    });
  }
}


export class Button { // * CSS required
  constructor(selectors) { // selectors = buttons
    this.selectors = selectors;
  }

  // 리플 효과
  // @param {Event} event - 클릭 이벤트 객체
  #setRipple(event) {
    const button = event.currentTarget;
    const $button = jQuery(button);

    // CSS ::after 가상 요소 없으면 리플 효과 적용하지 않음
    if ( window.getComputedStyle(button, '::after').content === 'none' ) {
      return;
    }

    // 클릭 좌표와 버튼 크기를 기반으로 리플 효과의 크기와 위치 계산
    const { clientX, clientY } = event;
    const { x, y, width, height } = button.getBoundingClientRect();
    const radius = Math.hypot(width, height); // 대각선 길이를 반지름으로 사용

    Cssvar.set({ // 계산된 값을 CSS 변수로 설정
      '--js-ripple-x': `${clientX - x}px`,
      '--js-ripple-y': `${clientY - y}px`,
      '--js-ripple-diameter': `${radius * 2}px`,
    });

    $button.datattr({ pressed: '' });  // 애니메이션 위해 [data-pressed] 추가
  }

  // 리플 애니메이션 종료 시 관련 효과 정리
  // @param {Event} event - 애니메이션 종료 이벤트 객체
  #unsetRipple(event) {
    jQuery(event.currentTarget).removeDatattr('pressed'); // 데이터 속성 제거
    Cssvar.delete(
      ['--js-ripple-x', '--js-ripple-y', '--js-ripple-diameter']
    );
  }

  watch() {
    // ripple start -> end
    $doc.on('click', this.selectors, (event) => this.#setRipple(event) );
    $doc.on('animationend webkitAnimationEnd', this.selectors, (event) =>
      this.#unsetRipple(event)
    );
  }
}


export class ButtonQuantity {
  constructor(selector = '.button-quantity') {
    this.selector = selector;
  }

  // 그룹 인풋 합계 계산 및 max 재설정
  // 동일 그룹 인풋들의 합계를 계산하여 HTML 고유 속성(max)을 재설정하는 헬퍼
  #updateGroupMax($currentInput) {
    const $wrapper = $currentInput.closest(this.selector);
    let $inputs;
    let groupMax = 0;

    // 1. [신규] DOM이 흩어져 있을 때 '식별자(name)'로 묶는 방식
    // 개별 button-quantity 래퍼에 data-group-name 속성이 있는지 먼저 확인합니다.
    const groupName = $wrapper.data('groupName');

    if (groupName) {
      groupMax = parseInt($wrapper.data('groupMax'), 10) || 0;
      // 전역(document)에서 동일한 그룹명을 가진 모든 래퍼를 싹 다 찾아옵니다!
      $inputs = jQuery(`${this.selector}[data-group-name="${groupName}"]`).find('input[type="number"]');
    }
    // 2. [기존] 공통 부모 요소(Wrapper)로 묶여 있는 방식
    else {
      const $parentGroup = $currentInput.closest('[data-group-max]');
      if (!$parentGroup.length) return; // 둘 다 없으면 단독 동작으로 간주하고 종료

      groupMax = parseInt($parentGroup.data('groupMax'), 10) || 0;
      $inputs = $parentGroup.find(`${this.selector} input[type="number"]`);
    }

    // --- 아래 로직은 기존과 100% 동일합니다 ---
    let currentTotal = 0;
    $inputs.each((_, el) => currentTotal += parseInt(jQuery(el).value(), 10) || 0);

    $inputs.each((_, el) => {
      const $el = jQuery(el);
      const val = parseInt($el.value(), 10) || 0;

      let originalMax = $el.data('originalMax');
      if (originalMax === undefined) {
        originalMax = parseInt($el.max(), 10) || groupMax;
        $el.datattr({ originalMax });
      }

      const remaining = groupMax - currentTotal + val;
      const newMax = Math.min(originalMax, remaining);

      $el.max(newMax);
      $el.siblings('[data-action="plus"]').disabled(val >= newMax);
    });
  }

  // 버튼 클릭 핸들러
  #handleClick(e) {
    const $btn = jQuery(e.currentTarget);
    const action = $btn.data('action');
    const $input = $btn.siblings('input[type="number"]');

    let val = parseInt($input.value(), 10) || 0;
    const min = parseInt($input.min(), 10) || 0;
    const max = parseInt($input.max(), 10) || Infinity;

    // 한 줄 if-else 처리
    if (action === 'plus' && val < max) val++;
    else if (action === 'minus' && val > min) val--;

    // 익스텐션 .value()로 셋업 (내부적으로 change 이벤트 발생) 후 input 이벤트 강제 트리거
    $input.value(val).trigger('input');
  }

  // 인풋 값 변경 핸들러
  #handleInput(e) {
    const $input = jQuery(e.currentTarget);
    let val = parseInt($input.value(), 10) || 0;
    const min = parseInt($input.min(), 10) || 0;
    const max = parseInt($input.max(), 10) || Infinity;

    // 한 줄 if 처리로 방어 로직 압축
    if (val < min) val = min;
    if (val > max) val = max;
    if (parseInt($input.value(), 10) !== val) $input.value(val);

    const $wrapper = $input.closest(this.selector);
    $wrapper.find('[data-action="minus"]').disabled(val <= min);
    $wrapper.find('[data-action="plus"]').disabled(val >= max);

    this.#updateGroupMax($input);
  }

  watch() {
    // 이벤트 대상을 watch()에 명시하고, 실행은 분리된 메서드로 전달
    $doc.on('click', `${this.selector} button[data-action]`, (e) => this.#handleClick(e));
    $doc.on('input', `${this.selector} input[type="number"]`, (e) => this.#handleInput(e));

    // 페이지 로드 시 초기 상태 동기화
    jQuery(`${this.selector} input[type="number"]`).trigger('input');
  }
}


export class Tab { // * CSS required
  static #tab = '[role="tab"]';
  static #tabpanel = '[role="tabpanel"]';

  constructor(selector) { // selector = tab container
    this.selector = selector;
    this.$selector = jQuery(selector);
    // 인스턴스 생성 시점에 탭과 패널을 한 번만 탐색하여 캐싱
    this.$tabs = this.$selector.find(Tab.#tab);
    this.$tabpanels = this.$selector.find(Tab.#tabpanel);
  }

  // ? 해시와 직접 연결된 탭 찾기
  // @param {string} hash - 'tabpanel-' 접두사가 없는 해시 값
  // @returns {jQuery|null}
  #getTabByHash(hash) {
    const panelID = `tabpanel-${hash}`;
    const $targetTab = this.$tabs.filter(`[aria-controls="${panelID}"]`);
    return $targetTab.be() ? $targetTab.first() : null;
  }

  // ? 해시 타겟 요소를 포함하고 있는 부모 탭 패널 찾기
  // @param {string} hash
  // @returns {jQuery|null}
  #getTabByInnerElement(hash) {
    try { // ? 해시 ID를 가진 요소가 페이지에 존재하는지 확인
      const $target = jQuery(`#${hash}`);
      if ( $target.be() ) { // ? 그 요소의 조상 중에 탭 패널이 있는지 확인
        const $tabpanel = $target.closest(Tab.#tabpanel);
        if ( $tabpanel.be() ) { // ? 해당 패널을 제어하는 탭 버튼 반환
          const panelID = $tabpanel.id();
          return this.$tabs.filter(`[aria-controls="${panelID}"]`).first();
        }
      }
    }
    catch (e) {
      return null; // ? 유효하지 않은 선택자 오류 방지
    }
    return null;
  }

  // ? 통합 탭 탐색기
  #findTargetTab(hash) {
    // * 1순위: 탭 자체를 가리키는 경우
    let $tab = this.#getTabByHash(hash);
    // * 2순위: 탭 내부의 요소를 가리키는 경우 (DetailsDeepLink 등과의 호환성)
    if ( !$tab?.be() ) $tab = this.#getTabByInnerElement(hash);
    // * 3순위: data-slug로 내부 요소를 찾는 경우 (DetailsDeepLink 호환)
    if ( !$tab?.be() ) {
      // data-slug="hash"인 요소가 있는지 확인
      const $slug = jQuery(`[data-slug="${hash}"]`);
      if ( $slug.be() ) {
        // 그 요소의 조상 탭 패널을 찾음
        const $tabpanel = $slug.closest(Tab.#tabpanel);
        if ( $tabpanel.be() ) {
          const panelID = $tabpanel.id();
          $tab = this.$tabs.filter(`[aria-controls="${panelID}"]`).first();
        }
      }
    }
    return $tab;
  }

  // 특정 탭 활성화하고 나머지 탭과 패널은 비활성화
  // @param {jQuery} $tabToActivate - 활성화할 탭
  // @param {boolean} [doUpdateHash=true] - URL 해시를 업데이트할지 여부
  #activateTab($tabToActivate, doUpdateHash = true, doScroll = true) {
    const panelID = $tabToActivate.aria('controls');

    this.$tabs.aria('selected', 'false').tabindex(-1); // 모든 탭 비활성화/초기화
    $tabToActivate.aria('selected', 'true').tabindex(0); // 대상 탭만 활성화

    this.$tabpanels.hidden(true); // 모든 패널을 숨김 처리 후
    jQuery(`#${panelID}`).hidden(false); // 대상 패널만 표시

    let currentSlug = '';

    if ( doUpdateHash && panelID ) { // URL 해시 업데이트
      const hash = panelID.replace('tabpanel-', '');
      currentSlug = hash === 'all' ? '' : hash; // 슬러그 정규화

      history.replaceState(
        null, '', currentSlug ? `#${currentSlug}` : window.location.pathname
      );
    }
    else if ( panelID ) {
      // URL 업데이트를 안 하더라도 현재 어떤 탭인지 슬러그는 추출
      const hash = panelID.replace('tabpanel-', '');
      currentSlug = hash === 'all' ? '' : hash;
    }

    // * [추가] 탭 변경 사실을 전역에 알림 (이벤트를 통해 FooterDoormat과 소통)
    $win.trigger('tab:change', [currentSlug]);

    if ( doScroll ) $tabToActivate.smartScroll();
  }

  #handleClick(event) {
    event.preventDefault();
    // 1. 현재 페이지가 상세 보기(Detail View) 모드인지 확인 (주소에 /@/ 포함 여부)
    if ( window.location.pathname.includes('/@/') ) {
      const $target = jQuery(event.currentTarget);
      const panelID = $target.aria('controls');
      const hash = panelID.replace('tabpanel-', '');

      // 2. 이동할 기본 경로 계산 (/locations/@/slug -> /locations)
      const cleanPath = window.location.pathname.replace(/\/@\/.*$/, '');

      // 3. 이동할 해시 계산 (all이면 해시 없음, 그 외에는 #국가코드)
      const targetHash = hash === 'all' ? '' : `#${hash}`;

      // 4. 페이지 강제 이동 (새로고침 발생 -> PHP가 깨끗한 리스트를 다시 렌더링함)
      window.location.href = cleanPath + targetHash;
      return; // 더 이상 JS 로직 실행하지 않고 종료
    }

    // 5. 일반 리스트 모드라면, 기존처럼 부드럽게 JS로 탭 전환 (새로고침 안 함)
    this.#activateTab(jQuery(event.currentTarget), true, true);
  }
  #handleKeyDown(event) {
    const $currentTab = jQuery(event.currentTarget);
    const currentIndex = this.$tabs.index($currentTab);
    const tabCount = this.$tabs.length;
    let $nextTab;

    switch ( event.key ) {
      case 'ArrowRight':
        $nextTab = this.$tabs.eq((currentIndex + 1) % tabCount);
        break;
      case 'ArrowLeft':
        $nextTab = this.$tabs.eq((currentIndex - 1 + tabCount) % tabCount);
        break;
      case 'Home':
        $nextTab = this.$tabs.first();
        break;
      case 'End':
        $nextTab = this.$tabs.last();
        break;
      default:
        return; // 다른 키는 무시
    }

    event.preventDefault();

    // 실제 탭 활성화하는 건 handleClick() 함수로 이관 (코드 중복 피하고 일관성 유지)
    $nextTab.trigger('click').focus();
  }
  #handleHashChange() {
    const hash = window.location.hash.slice(1);

    if ( hash ) {
      const $tab = this.#findTargetTab(hash);

      if ( $tab?.be() ) {
        // [CASE A] 유효한 탭인 경우
        this.#activateTab($tab, false, true);
      }
      else {
        // [CASE B] 유효하지 않은 해시 -> Clean URL & Reset
        history.replaceState(null, '', window.location.pathname);

        const $defaultTab = this.$tabs.first();
        this.#activateTab($defaultTab, false, true);

        // [추가] 브라우저 포커스를 강제로 기본 탭으로 이동시킴
        // 이를 통해 이전 요소에 남아있던 :focus 상태를 제거하고
        // 스크린 리더나 키보드 사용자에게 현재 위치를 명확히 알림
        $defaultTab.focus();
      }
    }
    else {
      // [CASE C] 해시 삭제됨
      const $defaultTab = this.$tabs.first();
      this.#activateTab($defaultTab, false, true);
      // [추가] 여기도 마찬가지로 포커스 이동 (선택 사항이나 권장함)
      $defaultTab.focus();
    }
  }

  // 페이지 로드 시 초기 탭 설정
  #init() {
    const hash = window.location.hash.slice(1); // '#' 제거
    let $initialTab = null;
    let shouldScroll = false;
    let doUpdateHash = true;

    if ( hash ) {
      // 1. 해시로 탭 찾기 시도
      $initialTab = this.#findTargetTab(hash);

      if ( $initialTab?.be() ) {
        // [CASE A] 유효한 해시인 경우
        shouldScroll = true;
        // 사용자가 명시적으로 들어왔으므로 URL 유지 (덮어쓰기 금지)
        doUpdateHash = false;
      }
      else {
        // [CASE B] 유효하지 않은 해시(예: #trash)인 경우 -> [수정]
        // 1. 탭을 찾지 못했으므로 $initialTab은 null 상태 유지
        // 2. 아래 로직에서 기본 탭(All)이 선택됨
        // 3. 잘못된 해시를 제거하기 위해 URL 업데이트 강제 수행
        doUpdateHash = true;

        // * [SEO/UX] 잘못된 해시 즉시 제거 (Clean URL)
        // 사용자가 #trash로 들어왔지만, 실제로는 기본 탭을 보게 되므로
        // 주소창에서도 #trash를 지워주는 것이 정석입니다.
        history.replaceState(null, '', window.location.pathname);
      }
    }

    // 초기 탭이 없거나(해시 없음) 잘못된 해시였을 경우 -> 기본 탭(All) 선택
    if ( !$initialTab?.be() ) {
      $initialTab = this.$tabs.filter('[aria-selected="true"]').first();

      // 만약 해시가 아예 없어서 기본 탭으로 온 거라면 URL 업데이트 불필요
      if ( !hash ) doUpdateHash = false;
    }

    if ( $initialTab.be() ) {
      // 탭 활성화 (잘못된 해시였다면 여기서 All 탭이 활성화되며 초기화됨)
      this.#activateTab($initialTab, doUpdateHash, shouldScroll);
    }
  }

  watch() {
    this.#init();
    this.$selector.on('click', Tab.#tab, (event) =>
      this.#handleClick(event)
    );
    this.$selector.on('keydown', Tab.#tab, (event) =>
      this.#handleKeyDown(event)
    );
    $win.on('hashchange', () => this.#handleHashChange());
  }
}


export class Card { // * CSS required
  static #card = '.card';

  constructor(selectors) { this.selectors = selectors; } // selectors = cards

  // @param {jQuery} $cards - 스타일을 적용할 카드 컨테이너 요소
  #init($cards) {
    const $card = $cards.find(Card.#card);
    const cardCount = $card.length;
    const minWidth = $cards.data('cardMinWidth'); // 컨테이너의 데이터 속성 읽기

    // 카드 개수 4개 이상(4, 5, 6)일 때 data-card-count 속성 추가
    if ( 4 <= cardCount ) $cards.datattr({ cardCount });

    // * 카드 개수 4개 미만이고 minWidth 지정되었을 때 grid-template-columns 설정
    if ( cardCount < 4 && isCssSize(minWidth) ) {
      $cards.css(
        'grid-template-columns',
        `repeat(auto-fit, minmax(min(${minWidth}, 100%), 1fr))`
      );
    }
  }

  watch() {
    jQuery(this.selectors).each((_, elem) => this.#init(jQuery(elem)) );
  }
}


export class Tooltip {
  static #safeSpace = 20; // px
  static #fadeSpeed = 200;

  constructor(selectors) { this.selectors = selectors; }

  static #getId() {
    return 'js-tooltip-id-' + Math.random().toString(36).substring(2, 10);
  }

  // 🌟 [수정] 4번째 파라미터로 extraClass 추가
  static #render($appendTo, message, $trigger, extraClass = '') {
    // Tooltip.remove();

    const id = Tooltip.#getId();
    // 1. 우선 임시 위치에 렌더링 (측정을 위해 투명도 0으로 시작)
    extraClass = extraClass !== '' ? ` class="${extraClass}"` : '';
    const $tooltip = jQuery(`<div role="tooltip" id="${id}"${extraClass} style="opacity: 0; pointer-events: none;">${message}</div>`);

    // 🌟 [핵심 수정] <input> 등 자식을 가질 수 없는 요소인 경우 분기 처리
    if ( $appendTo.is('input, textarea, select, img, br, hr') ) {
      $tooltip.insertAfter($appendTo);
    }
    else if ( $appendTo.get(0) instanceof SVGElement ) {
      const $svgWrapper = $appendTo.closest('svg').parent();
      $tooltip.appendTo($svgWrapper);
    }
    else $tooltip.appendTo($appendTo);

    // 🌟 [추가] 툴팁이 절대 위치를 잘 잡을 수 있도록 부모 요소를 relative로 보정
    if ( $tooltip.parent().css('position') === 'static' ) {
      $tooltip.parent().css('position', 'relative');
    }

    const triggerEl = $trigger.get(0);
    const tooltipEl = $tooltip.get(0);
    const rect = triggerEl.getBoundingClientRect();
    const tw = tooltipEl.offsetWidth;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // 2. 수직 위치 결정
    const shouldShowBelow = rect.top < 100;
    const vInset = shouldShowBelow ? 'inset-block-start: 100%' : 'inset-block-end: 100%';
    const vTrans = shouldShowBelow ? '0.5em' : '-0.5em';

    // 🌟 CSS에서 말풍선 꼬리 방향을 알 수 있도록 클래스 이름 결정
    const tailClass = shouldShowBelow ? 'is-below' : 'is-above';

    // 3. 수평 위치 결정 (실제 툴팁 너비 'tw'를 계산에 넣음)
    let hInset = 'inset-inline-start: 50%';
    let hTrans = '-50%';

    // 좌측 이탈 방지
    if ( (rect.left + rect.width / 2) - (tw / 2) < Tooltip.#safeSpace ) {
      // 트리거 중앙에서 툴팁 절반을 뺀 값이 safeSpace보다 작으면
      // 툴팁을 화면 왼쪽 끝(safeSpace)에 고정시키기 위해 트리거로부터의 거리 계산
      const offsetLeft = rect.left - Tooltip.#safeSpace;
      hInset = 'inset-inline-start: 0';
      hTrans = `calc(-${offsetLeft}px)`; // 트리거의 left 위치만큼 왼쪽으로 역이동
    }
    // 우측 이탈 방지
    else if ( (rect.left + rect.width / 2) + (tw / 2) > vw - Tooltip.#safeSpace ) {
      const offsetRight = (vw - rect.right) - Tooltip.#safeSpace;
      hInset = 'inset-inline-start: 100%';
      hTrans = `calc(-100% + ${offsetRight}px)`;
    }

    // 4. 최종 스타일 적용 및 페이드인
    // 🌟 attr() 앞에 addClass(tailClass) 추가
    $tooltip.addClass(tailClass).attr('style', `${vInset}; ${hInset}; translate: ${hTrans} ${vTrans}; opacity: 0; pointer-events: none;`)
      .animate({ opacity: 1 }, Tooltip.#fadeSpeed);

    $trigger.aria('describedby', id);
  }

  // 🌟 [수정] 3번째 파라미터로 extraClass를 받아 #render로 전달
  static create($target, message, extraClass = '') {
    if ( $target.be(false) || !message ) return;
    Tooltip.#render($target, message, $target, extraClass);
  }

  #show(event) {
    const $trigger = jQuery(event.currentTarget);
    const message = $trigger.data('tooltip');
    if ( !message ) return;

    Tooltip.#render($trigger, message, $trigger);
  }

  // 🌟 [수정] 특정 타겟이 지정되면 그 녀석의 툴팁만 지우는 스마트 핀셋 삭제 기능 추가
  static remove($target = null) {
    // 만약 특정 인풋($target)이 넘어왔다면, 그 인풋에 걸린 툴팁만 조준 사격해서 지웁니다.
    if ( $target && $target.be() ) {
      const id = $target.aria('describedby');
      if ( id ) {
        // 🌟 툴팁(role=tooltip)만 지움 : 도움말(.helptext) 등 직접 연결해 둔 aria-describedby 대상은 건드리지 않음
        const $tooltip = jQuery(`#${id}[role="tooltip"]`);
        if ( $tooltip.be() ) {
          $tooltip.fadeOutRemove(Tooltip.#fadeSpeed);
          $target.removeAttr('aria-describedby');
        }
      }
      return;
    }

    // $target이 없으면 기존처럼 화면상의 모든 툴팁을 싹 다 날려버립니다 (전체 청소)
    const $tooltips = jQuery('[role=tooltip]');
    if ( $tooltips.be(false) ) return;

    $tooltips.each((_, el) => {
      const id = jQuery(el).id();
      if ( id ) {
        jQuery(`[aria-describedby="${id}"]`).removeAttr('aria-describedby');
      }
    });

    $tooltips.fadeOutRemove(Tooltip.#fadeSpeed);
  }

  watch() {
    $doc.on('mouseenter', this.selectors, (event) => this.#show(event));
    $doc.on('mouseleave', this.selectors, () => Tooltip.remove());
  }
}


export class Footnote { // * CSS required
  constructor(selectors) { this.selectors = selectors; } // selectors = <a>

  // 클릭 이벤트 처리하여 부드러운 스크롤 실행
  #handleClick(event) {
    // 브라우저 기본 동작(해시 변경 및 즉시 이동) 막기
    // ? 사실상 핵심 : 주소 표시줄에 해시 태크가 안 보이게 함 (이 클래스 없으면 노출됨)
    event.preventDefault();

    const targetID = jQuery(event.currentTarget).href();
    if ( !targetID || !targetID.startsWith('#') ) return;

    const $target = jQuery(targetID);
    if ( $target.be(false) ) return;

    $target.smartScroll({ block: 'center' }); // 타겟 요소 실존 시 스크롤 실행
  }

  watch() {
    $doc.on('click', this.selectors, (event) => this.#handleClick(event));
  }
}


export class DetailsDeepLink { // for details
  constructor(selector = 'details[data-slug]') {
    this.selector = selector;
    this.tabpanelAll = 'tabpanel-all';
  }

  // URL 해시를 기반으로 '전체' 탭에서 해당 질문 찾아 열기
  #openByHash() {
    let rawHash = window.location.hash;
    const slug = rawHash ?
      decodeURIComponent(rawHash).trim().replace(/^#/, '') : '';

    // 슬러그가 없으면 여기서 종료 (질문 열기 로직 불필요)
    if ( !slug ) return;

    // ? 핵심 로직 : 전체 탭패널에 있는 질문 먼저 찾기
    const target = `details[data-slug='${slug}']`;
    let $target = jQuery(`#${this.tabpanelAll} ${target}`);
    // ? 전체 탭패널에 없으면 다른 탭으로
    if ( $target.be(false) ) $target = jQuery(target).first();
    if ( $target.be(false) ) return; // 해당 슬러그 요소 없으면 종료

    // 타겟이 속한 탭패널 찾기
    const $tabpanel = $target.closest('[role=tabpanel]');
    // 탭 닫혀있다면(hidden 속성이 있다면), 해당 탭 버튼을 찾아 클릭(활성화)
    if ( $tabpanel.be() && $tabpanel.attr('hidden') ) {
      const tabpanelID = $tabpanel.id();
      jQuery(`button[aria-controls="${tabpanelID}"]`).trigger('click');
    }

    // 질문 열기 (브라우저가 렌더링하도록 open 속성 부여)
    $target.open(true);

    // 스크롤 이동 (약간의 지연을 주어 UI가 다 펼쳐진 뒤 이동)
    $target.smartScroll({ block: 'center', delay: 150 });
  }

  // 사용자가 질문을 열었을 때 URL 해시를 업데이트
  #handleToggle(event) {
    const details = event.currentTarget;
    // 열릴 때만 해시 변경 (닫을 때는 URL 유지)
    if ( details.open && details.dataset.slug ) {
      const slug = details.dataset.slug;

      // 현재 해시와 다를 때만 변경 (중복 히스토리 방지)
      if ( window.location.hash !== `#${slug}` ) {
        history.pushState(null, '', `#${slug}`);
      }
    }
  }

  watch() {
    const rawHash = window.location.hash;

    // * [핵심 로직] 해시 끝에 슬래시(/)가 있다면 강제 새로고침
    if ( rawHash && rawHash.endsWith('/') ) {
      const cleanHash = rawHash.replace(/\/+$/, '');
      history.replaceState(null, '', cleanHash);
      window.location.reload();
      return;
    }

    // [수정 4] 해시 유무와 관계없이 초기 로드 시 하이라이팅 로직 실행
    // (PHP가 처리하지만, JS단에서 확실하게 동기화)
    setTimeout(() => { this.#openByHash(); }, 50);

    // 'toggle' 이벤트 버블링되지 않으므로, body 대신 직접 선택자에 바인딩
    jQuery(this.selector).on('toggle', (event) => this.#handleToggle(event));

    // 뒤로/앞으로 가기 또는 해시 직접 변경 시 상태 동기화
    $win.on('popstate', () => this.#openByHash());
  }
}


export class FooterDoormat {
  constructor() { this.selector = 'footer .doormat'; }

  // 메뉴 하이라이팅 로직 (DetailsDeepLink에서 추출 및 최적화)
  #highlight(slug) {
    const $links = jQuery(`${this.selector} a`);
    $links.removeClass('selected');

    const currentPath = window.location.pathname.replace(/\/$/, '');

    $links.filter(function() {
      const linkPath = this.pathname.replace(/\/$/, '');
      const linkHash = this.hash;

      if ( linkPath !== currentPath ) return false;

      // slug가 있으면 해시 일치 여부, 없으면(All) 해시 없는지 확인
      return slug ? linkHash === `#${slug}` : !linkHash;
    }).addClass('selected');
  }

  watch() {
    // 1. 초기 로드 시 실행 (지연 없이 즉시 실행해도 됨)
    const initialHash = window.location.hash.replace(/^#/, '');
    this.#highlight(initialHash);

    // 2. [핵심] Tab 클래스가 보내는 'tab:change' 이벤트를 감지
    $win.on('tab:change', (e, slug) => { this.#highlight(slug); });

    // 3. 브라우저 뒤로가기/앞으로가기 감지
    $win.on('popstate', () => {
      const hash = window.location.hash.replace(/^#/, '');
      this.#highlight(hash);
    });
  }
}


export class Lightbox {
  static #lightbox = '[data-lightbox]';

  constructor(selector = '[data-lightbox-trigger]') {
    this.selector = selector;

    // 다이얼로그 관련 변수는 open 시점에 할당하므로 null로 초기화
    this.$dialog = null;
    this.$img = null;
    this.$caption = null;

    this.currentGroup = null;
    this.currentIndex = 0;
    this.items = [];
  }

  // [NEW] 서버에서 다이얼로그 HTML 가져와 삽입
  async #fetchTemplate() {
    try {
      // 이미 로드되어 있다면 중복 로드 방지 (혹은 매번 새로 받고 싶다면 이 체크 제거)
      if ( jQuery(Lightbox.#lightbox).be() ) return;

      const html = await jQuery.get('/js/ajax/lightbox.php');
      $body.append(html);

      // DOM 삽입 후 요소 캐싱
      this.$dialog = jQuery(Lightbox.#lightbox);
      this.$img = this.$dialog.find('figure > img');
      this.$caption = this.$dialog.find('figcaption');
    }
    catch (error) {
      Dialog.error(`Lightbox template load failed: ${error.status}`);
      throw error; // 에러 발생 시 open 중단
    }
  }

  async #open(trigger) {
    // 1. 템플릿 로드 및 삽입 (대기)
    await this.#fetchTemplate();

    // 2. 이후 로직은 기존과 동일하지만, this.$dialog가 이제 존재함
    const $trigger = jQuery(trigger);
    const groupID = $trigger.data('lightboxGroup');

    this.currentGroup = groupID;
    this.items = jQuery(`${this.selector}[data-lightbox-group="${groupID}"]`)
      .map((_, el) => {
        const $el = jQuery(el);
        const caption = $el.aria('label');
        const thumbAlt = $el.find('img').alt();
        return {
          src: $el.href(),
          alt: caption || thumbAlt || "",
          caption: caption
        };
      }).get();

    this.currentIndex = parseInt($trigger.data('lightboxTrigger')) || 0;

    this.#updateImage();

    // showModal 실행
    if ( this.$dialog && this.$dialog[0] ) {
      this.$dialog[0].showModal();
      Scroll.lock(this.$dialog);
      this.#bindDynamicEvents();
    }
  }

  #close() {
    if ( !this.$dialog ) return;

    const $dialog = this.$dialog;
    const dialog = $dialog[0];

    Scroll.unlock();
    this.#unbindDynamicEvents();

    const removeDialog = () => {
      if ( !$dialog || !$dialog.parent().length ) return;
      $dialog.remove();
    };

    // [수정 포인트] transitionend 이벤트 핸들러 개선
    // 1. .one() 대신 .on()을 사용하되 (잘못된 타겟의 이벤트로 리스너가 소멸되는 것 방지)
    // 2. e.target이 다이얼로그 본체일 때만 removeDialog 실행
    $dialog.on('transitionend', (event) => {
      if ( event.target === dialog ) removeDialog();
    });

    // 안전장치 (트랜지션이 안 일어나는 상황 대비)
    setTimeout(removeDialog, 550);

    dialog.close();

    this.$dialog = null;
    this.$img = null;
    this.$caption = null;
  }

  #updateImage() {
    // 방어 코드: 요소가 없으면 중단
    if ( !this.$img || this.items.length === 0 ) return;

    if ( this.currentIndex < 0 ) this.currentIndex = this.items.length - 1;
    if ( this.currentIndex >= this.items.length ) this.currentIndex = 0;

    const item = this.items[this.currentIndex];

    this.$img.css('opacity', 0);

    const newImg = new Image();
    newImg.src = item.src;

    newImg.onload = () => {
      // 닫히는 사이에 요소가 사라졌을 경우 대비
      if ( this.$img && this.$img.be() ) {
        this.$img.src(item.src).alt(item.alt);
        this.$img.animate({ opacity: 1 }, 200);
      }
    };

    if ( this.$caption ) {
      if ( item.caption ) this.$caption.text(item.caption).hidden(false);
      else this.$caption.text('').hidden(true);
    }
  }

  #navigate(direction) {
    this.currentIndex += direction;
    this.#updateImage();
  }

  #handleKeydown(event) {
    if ( !this.$dialog || !this.$dialog.is('[open]') ) return;

    switch ( event.key ) {
      case 'Escape':
        event.preventDefault();
        this.#close();
        break;
      case 'ArrowLeft': this.#navigate(-1); break;
      case 'ArrowRight': this.#navigate(1); break;
    }
  }

  #bindDynamicEvents() {
    if ( !this.$dialog ) return;

    $win.on('keydown.lightbox', (event) => this.#handleKeydown(event));

    this.$dialog.on('click.lightbox', '[data-lightbox-action]', (event) => {
      event.stopPropagation();
      const action = jQuery(event.currentTarget).data('lightboxAction');

      if ( action === 'close' ) this.#close();
      else if ( action === 'prev' ) this.#navigate(-1);
      else if ( action === 'next' ) this.#navigate(1);
    });

    this.$dialog.on('click.lightbox', (event) => {
      if ( event.target === this.$dialog[0] ) this.#close();
    });
  }

  #unbindDynamicEvents() {
    $win.off('keydown.lightbox');
    // $dialog.remove()를 하면 내부에 걸린 이벤트도 같이 삭제되지만,
    // 명시적으로 꺼주는 것이 안전합니다.
    if ( this.$dialog ) this.$dialog.off('click.lightbox');
  }

  watch() {
    $doc.on('click', this.selector, (event) => {
      event.preventDefault();
      this.#open(event.currentTarget);
    });
  }
}


export class Loading {
  static #id = 'loading';
  static #duration = 300; // [NEW] 트랜지션 속도를 단일 변수로 관리

  static show() {
    if ( jQuery(`#${Loading.#id}`).be(false) ) {
      const html = `
        <div id="${Loading.#id}" class="loading-overlay" aria-hidden="true">
          <div class="loading-spinner"></div>
        </div>
      `;
      $body.fadeInPrepend(html, Loading.#duration);
    }
  }

  static hide() {
    // 1. 화면의 로딩 오버레이 DOM을 페이드아웃 후 삭제
    jQuery(`#${Loading.#id}`).fadeOutRemove(Loading.#duration);

    // 2. 초기 하드코딩된 <style> 태그가 존재하면, 애니메이션 시간이 끝난 후 깔끔하게 삭제
    const $initialStyle = jQuery('#initial-loading-style');
    if ( $initialStyle.be() ) {
      setTimeout(() => { $initialStyle.remove(); }, Loading.#duration);
    }
  }
}


export class BackToTop {
  static #threshold = 300; // 스크롤 노출 기준점

  constructor(selectors) {
    this.selectors = selectors;
  }

  // @param {jQuery} button - 스타일 및 이벤트를 적용할 버튼 요소
  #init($btn) {
    // 1. 스크롤 위치에 따른 버튼 노출 및 숨김
    jQuery(window).on('scroll', () => {
      const scrollY = jQuery(window).scrollTop();

      // 코드가 짧아 중괄호 없이 한 줄 처리 및 else 줄 바꿈 규칙 적용
      if ( scrollY > BackToTop.#threshold ) $btn.addClass('is-visible');
      else $btn.removeClass('is-visible');
    });

    // 2. 버튼 클릭 시 최상단으로 스크롤 이동
    $btn.on('click', (e) => {
      e.preventDefault();

      // 최신 Web API를 사용한 부드러운 스크롤 구현
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    });
  }

  watch() {
    jQuery(this.selectors).each((_, elem) => this.#init(jQuery(elem)) );
  }
}


export class NewsTicker {
  constructor(selectors = '[data-news-ticker]') {
    this.selectors = selectors;
  }

  // @param {jQuery} $list - 스타일 및 이벤트를 적용할 ul 요소
  #init($list) {
    if ( $list.children().length < 2 ) return;

    // 1. data 속성에서 interval 시간 추출 및 파싱
    const rawTime = String($list.data('newsTicker') || '3s');
    const interval = rawTime.includes('s') && !rawTime.includes('ms')
      ? parseFloat(rawTime) * 1000
      : parseFloat(rawTime);

    let timer = null;

    // 2. CSS 트랜지션 종료 감지 및 DOM 이동
    $list.on('transitionend', e => {
      if ( e.target !== e.currentTarget ) return;

      if ( $list.hasClass('is-sliding') ) {
        $list.children().first().appendTo($list);
        $list.removeClass('is-sliding');
      }
    });

    // 3. 타이머 제어 함수
    const play = () => timer = setInterval(() => $list.addClass('is-sliding'), interval);
    const pause = () => clearInterval(timer);

    play();

    // 4. 마우스 오버 시 일시 정지
    $list.parent().on('mouseenter', pause).on('mouseleave', play);

    // 5. 닫기 버튼 [data-news-ticker-close] 이벤트 바인딩
    // $list를 기준으로 가장 가까운 최상위 배너 컨테이너(aside)를 찾습니다.
    const $wrapper = $list.closest('aside.top-ads, aside');

    $wrapper.find('[data-news-ticker-close]').on('click', () => {
      // 24시간(86400초) 유지되는 쿠키 생성 (PHP의 !isset($_COOKIE["hide_top_ads"]) 검사와 매칭)
      document.cookie = "hide_top_ads=1; path=/; samesite=lax";

      pause(); // 백그라운드에서 돌아가는 setInterval 타이머 정리 (메모리 누수 방지)
      $wrapper.remove(); // 화면에서 요소 즉시 삭제
    });
  }

  watch() {
    jQuery(this.selectors).each((_, elem) => this.#init(jQuery(elem)) );
  }
}